import { useState, useRef, useEffect, useCallback } from "react";
import "./CourseCmsTab.css";
import {
  BookOpen, Plus, Upload, CheckCircle2, XCircle,
  Database, ChevronRight, FileJson, RefreshCw,
  FlaskConical, Sparkles, Brain, Terminal, Copy, Check,
  ChevronDown, ChevronUp, Pencil, ShieldAlert,
  AlertTriangle, X, Save, Eye, EyeOff, Loader2,
  Info, Zap, AlertCircle, Trash2, GripVertical
} from "lucide-react";
import IndividualPromptGenerator from './IndividualPromptGenerator';
import LessonAudit from './LessonAudit';


/* ════════════════════════════════════════════════════
   CONSTANTS & CONFIG
════════════════════════════════════════════════════ */


const LEVELS = [
  { id:"A1", label:"A1", name:"Básico",            color:"#10b981" },
  { id:"A2", label:"A2", name:"Básico Avanzado",  color:"#06b6d4" },
  { id:"B1", label:"B1", name:"Intermedio",       color:"#6366f1" },
  { id:"B2", label:"B2", name:"Interm. Avanzado", color:"#f59e0b" },
  { id:"C1", label:"C1", name:"Avanzado",         color:"#f97316" },
  { id:"C2", label:"C2", name:"Dominio",          color:"#ec4899" },
];

const EX_TYPE_META = {
  grammar_notes:             {label:"Grammar Notes",     color:"#6366f1",emoji:"📖"},
  quiz:                      {label:"Quiz",              color:"#06b6d4",emoji:"❓"},
  fill_in_the_blank:         {label:"Fill in the Blank", color:"#10b981",emoji:"✏️"},
  matching:                  {label:"Matching",          color:"#f59e0b",emoji:"🔗"},
  listening:                 {label:"Listening",         color:"#ec4899",emoji:"🎧"},
  pronunciation:             {label:"Pronunciation",     color:"#f97316",emoji:"🎙️"},
  error_detection:           {label:"Error Detection",   color:"#ef4444",emoji:"🔍"},
  reading:                   {label:"Reading",           color:"#8b5cf6",emoji:"📚"},
  writing:                   {label:"Writing",           color:"#14b8a6",emoji:"✍️"},
  conversation_spacy_prompt: {label:"Spacy AI Prompt",   color:"#00FFFF",emoji:"🤖"},
};

/* ════════════════════════════════════════════════════
   🔬 ESQUEMA CANÓNICO — La "Lupa" de Validación
════════════════════════════════════════════════════ */

const CANONICAL_SEQUENCE = [
  "conversation_spacy_prompt",
  "grammar_notes",
  "quiz",
  "fill_in_the_blank",
  "matching",
  "listening",
  "pronunciation",
  "error_detection",
  "reading",
  "writing",
];

function deepValidateExercises(arr) {
  const errors = [];

  arr.forEach((ex, i) => {
    const pos  = ex.position ?? i + 1;
    const type = ex.exercise_type;
    const d    = ex.data || {};
    const tag  = `[pos ${pos} · ${type}]`;

    // ── Root fields ──────────────────────────────────────
    if (!ex.exercise_type) errors.push(`[pos ${pos}] Falta 'exercise_type'`);
    if (!ex.lesson_id)     errors.push(`[pos ${pos}] Falta 'lesson_id'`);
    if (!ex.data || typeof ex.data !== "object")
      errors.push(`[pos ${pos}] Falta o inválido 'data'`);

    // ── Sequence check ───────────────────────────────────
    const expectedType = CANONICAL_SEQUENCE[i];
    if (expectedType && type !== expectedType)
      errors.push(`${tag} Tipo incorrecto. Esperado '${expectedType}' en pos ${i+1}`);

    // ── Per-type deep checks ─────────────────────────────
    switch (type) {

      case "conversation_spacy_prompt": {
        if (!Array.isArray(d.learning_objectives) || d.learning_objectives.length < 4)
          errors.push(`${tag} 'learning_objectives' debe ser array con ≥4 items`);
        if (!Array.isArray(d.visual_gallery_cards) || d.visual_gallery_cards.length < 1)
          errors.push(`${tag} 'visual_gallery_cards' debe ser array con ≥1 item`);
        (d.visual_gallery_cards||[]).forEach((c,ci) => {
          if (!c.trigger_topic || !c.card_id || !c.content)
            errors.push(`${tag} visual_gallery_cards[${ci}]: falta trigger_topic / card_id / content`);
        });
        break;
      }

      case "grammar_notes": {
        if (!Array.isArray(d.points) || d.points.length < 2)
          errors.push(`${tag} 'points' debe ser array con ≥2 items`);
        (d.points||[]).forEach((p,pi) => {
          if (!p.header || !p.content)
            errors.push(`${tag} points[${pi}]: falta 'header' o 'content'`);
        });
        break;
      }

      case "quiz": {
        if (!Array.isArray(d.questions) || d.questions.length < 5)
          errors.push(`${tag} 'questions' debe tener ≥5 items (tiene ${(d.questions||[]).length})`);
        (d.questions||[]).forEach((q,qi) => {
          if (!q.id || !q.question || !Array.isArray(q.options) || !q.answer)
            errors.push(`${tag} questions[${qi}]: falta id / question / options / answer`);
          if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
            errors.push(`${tag} questions[${qi}]: 'answer' ("${q.answer}") NO está en 'options' — debe ser texto exacto`);
          if (typeof q.answer === "number")
            errors.push(`${tag} questions[${qi}]: 'answer' es número — debe ser el TEXTO de la opción`);
        });
        break;
      }

      case "fill_in_the_blank": {
        if (!Array.isArray(d.blanks) || d.blanks.length < 5)
          errors.push(`${tag} 'blanks' debe tener ≥5 items (tiene ${(d.blanks||[]).length})`);
        (d.blanks||[]).forEach((b,bi) => {
          if (!b.id || !b.before_blank || !b.answer)
            errors.push(`${tag} blanks[${bi}]: falta id / before_blank / answer`);
          if (!b.hint) errors.push(`${tag} blanks[${bi}]: se recomienda 'hint'`);
        });
        break;
      }

      case "matching": {
        if (!Array.isArray(d.pairs)   || d.pairs.length   < 5) errors.push(`${tag} 'pairs' debe tener ≥5 items`);
        if (!Array.isArray(d.matches) || d.matches.length < 5) errors.push(`${tag} 'matches' debe tener ≥5 items`);
        if (!d.solution || typeof d.solution !== "object")
          errors.push(`${tag} Falta 'solution' (objeto { pairId: matchId })`);
        else {
          const pairIds  = (d.pairs  ||[]).map(p=>p.id);
          const matchIds = (d.matches||[]).map(m=>m.id);
          Object.entries(d.solution).forEach(([k,v]) => {
            if (!pairIds.includes(k))
              errors.push(`${tag} solution: key '${k}' no existe en pairs`);
            if (!matchIds.includes(v))
              errors.push(`${tag} solution: value '${v}' no existe en matches`);
          });
          if (Object.keys(d.solution).length !== (d.pairs||[]).length)
            errors.push(`${tag} solution tiene ${Object.keys(d.solution).length} entradas pero pairs tiene ${(d.pairs||[]).length}`);
        }
        break;
      }

      case "listening": {
        if (!d.audio_url)  errors.push(`${tag} Falta 'audio_url'`);
        if (!d.transcript) errors.push(`${tag} Falta 'transcript'`);
        if (!Array.isArray(d.questions) || d.questions.length < 5)
          errors.push(`${tag} 'questions' debe tener ≥5 items`);
        (d.questions||[]).forEach((q,qi) => {
          if (!q.id || !q.question || !Array.isArray(q.options) || !q.answer)
            errors.push(`${tag} questions[${qi}]: falta id / question / options / answer`);
          if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
            errors.push(`${tag} questions[${qi}]: 'answer' ("${q.answer}") NO está en 'options'`);
        });
        break;
      }

      case "pronunciation": {
        if (!Array.isArray(d.words_or_sentences) || d.words_or_sentences.length < 8)
          errors.push(`${tag} 'words_or_sentences' debe tener ≥8 items (tiene ${(d.words_or_sentences||[]).length})`);
        (d.words_or_sentences||[]).forEach((w,wi) => {
          if (!w.text)     errors.push(`${tag} words_or_sentences[${wi}]: falta 'text'`);
          if (!w.phonetic) errors.push(`${tag} words_or_sentences[${wi}]: falta 'phonetic' (IPA)`);
        });
        break;
      }

      case "error_detection": {
        if (!Array.isArray(d.sentences) || d.sentences.length < 5)
          errors.push(`${tag} 'sentences' debe tener ≥5 items`);
        (d.sentences||[]).forEach((s,si) => {
          if (!s.id || !s.sentence || !s.answer || !s.explanation)
            errors.push(`${tag} sentences[${si}]: falta id / sentence / answer / explanation`);
          if (s.answer !== "Correct" && s.answer !== "Incorrect")
            errors.push(`${tag} sentences[${si}]: 'answer' debe ser "Correct" o "Incorrect" (mayúsculas exactas), tiene "${s.answer}"`);
        });
        break;
      }

      case "reading": {
        if (!d.text || d.text.length < 80)
          errors.push(`${tag} 'text' debe existir y tener ≥80 chars`);
        if (!d.comprehension_quiz || !Array.isArray(d.comprehension_quiz.questions))
          errors.push(`${tag} Falta 'comprehension_quiz.questions'`);
        else if (d.comprehension_quiz.questions.length < 5)
          errors.push(`${tag} 'comprehension_quiz.questions' debe tener ≥5 items`);
        (d.comprehension_quiz?.questions||[]).forEach((q,qi) => {
          if (!q.id || !q.question || !Array.isArray(q.options) || !q.answer)
            errors.push(`${tag} comprehension_quiz.questions[${qi}]: falta id / question / options / answer`);
          if (q.answer && Array.isArray(q.options) && !q.options.includes(q.answer))
            errors.push(`${tag} comprehension_quiz.questions[${qi}]: 'answer' NO está en 'options'`);
        });
        break;
      }

      case "writing": {
        if (!d.theme)       errors.push(`${tag} Falta 'theme'`);
        if (!d.instruction) errors.push(`${tag} Falta 'instruction'`);
        break;
      }

      default:
        errors.push(`[pos ${pos}] Tipo desconocido: '${type}'`);
    }
  });

  return errors;
}

/* ════════════════════════════════════════════════════
   ⚙️ (aquí vivía el validador del espejo de Spacy — retirado el 29-08-2026)
════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════
   🔍 validateJSON — Análisis completo de estructura
════════════════════════════════════════════════════ */

function validateJSON(raw) {
  try {
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return {ok:false, syntaxOk:true, error:"Debe ser un array JSON."};

    const count      = arr.length;
    const types      = [...new Set(arr.map(e=>e.exercise_type).filter(Boolean))];
    const lessonIds  = [...new Set(arr.map(e=>e.lesson_id).filter(Boolean))];
    const positions  = arr.map(e=>e.position).filter(n=>typeof n==="number").sort((a,b)=>a-b);
    const hasAudio   = arr.some(e=>e.data&&e.data.audio_url);
    const audioUrl   = arr.find(e=>e.data&&e.data.audio_url);
    const hasImage   = arr.some(e=>e.data&&e.data.image_url);
    const totalXP    = arr.reduce((s,e)=>s+((e.data&&e.data.xp)||0),0);
    const hasSpacy   = arr.some(e=>e.exercise_type==="conversation_spacy_prompt");
    const allHaveType= arr.every(e=>e.exercise_type);
    const allHaveData= arr.every(e=>e.data&&typeof e.data==="object");
    const posOk      = positions.length===count&&new Set(positions).size===count;
    const lessonIdOk = lessonIds.length===1;
    const byType={};
    arr.forEach(e=>{if(e.exercise_type)byType[e.exercise_type]=(byType[e.exercise_type]||0)+1;});

    // ── Deep structural validation ────────────────────
    const deepErrors = deepValidateExercises(arr);
    const deepOk     = deepErrors.length === 0;

    // ── Count check ──────────────────────────────────
    const countOk = count === 10;

    const ok = countOk && lessonIdOk && posOk && allHaveType && allHaveData && deepOk;

    return {
      ok, syntaxOk:true, count, countOk, types, lessonIds, positions, posOk,
      hasAudio, audioUrl: audioUrl&&audioUrl.data&&audioUrl.data.audio_url,
      hasImage, totalXP, hasSpacy, allHaveType, allHaveData, lessonIdOk,
      byType, deepErrors, deepOk, parsed:arr
    };
  } catch(e) {
    return {ok:false, syntaxOk:false, error:e.message};
  }
}

/* ════════════════════════════════════════════════════
   🍞 TOAST SINGLETON — No más alert() nativos
════════════════════════════════════════════════════ */

let _toastSetter = null;

const nslToast = {
  success : (msg, dur=4000) => _toastSetter?.(p=>[...p, {id:Date.now()+Math.random(), msg, type:"success", dur}]),
  error   : (msg, dur=5000) => _toastSetter?.(p=>[...p, {id:Date.now()+Math.random(), msg, type:"error",   dur}]),
  warn    : (msg, dur=4500) => _toastSetter?.(p=>[...p, {id:Date.now()+Math.random(), msg, type:"warn",    dur}]),
  info    : (msg, dur=3500) => _toastSetter?.(p=>[...p, {id:Date.now()+Math.random(), msg, type:"info",    dur}]),
};

function ToastItem({toast, onDismiss, dark, t}) {
  const [visible, setVisible] = useState(false);
  useEffect(()=>{
    const tIn  = setTimeout(()=>setVisible(true),  20);
    const tOut = setTimeout(()=>{setVisible(false);setTimeout(()=>onDismiss(toast.id),320);}, toast.dur);
    return()=>{clearTimeout(tIn);clearTimeout(tOut);};
  },[]);

  const cfg = {
    success:{color:"#10b981", icon:<CheckCircle2 size={14}/>, bg:"#10b98118"},
    error:  {color:"#ef4444", icon:<XCircle      size={14}/>, bg:"#ef444418"},
    warn:   {color:"#f59e0b", icon:<AlertTriangle size={14}/>, bg:"#f59e0b18"},
    info:   {color:"#6366f1", icon:<Info          size={14}/>, bg:"#6366f118"},
  }[toast.type]||{color:"#00FFFF",icon:<Zap size={14}/>,bg:"#00FFFF18"};

  return (
    <div onClick={()=>{setVisible(false);setTimeout(()=>onDismiss(toast.id),320);}}
      style={{
        display:"flex",alignItems:"flex-start",gap:10,padding:"11px 14px",
        borderRadius:13,cursor:"pointer",userSelect:"none",
        background: dark ? (cfg.bg) : (cfg.bg),
        border:`1px solid ${cfg.color}44`,
        boxShadow:`-4px 4px 12px rgba(0,0,0,.25), 0 0 0 0.5px ${cfg.color}33`,
        backdropFilter:"blur(12px)",
        transform: visible ? "translateX(0) scale(1)" : "translateX(120%) scale(.92)",
        opacity: visible ? 1 : 0,
        transition:"transform .28s cubic-bezier(.34,1.2,.64,1), opacity .28s ease",
        maxWidth:340, minWidth:220, pointerEvents:"all",
      }}>
      <span style={{color:cfg.color,flexShrink:0,marginTop:1}}>{cfg.icon}</span>
      <span style={{fontSize:11.5,fontWeight:600,color: dark?"rgba(255,255,255,.88)":"rgba(0,0,0,.8)",
                    lineHeight:1.5,flex:1,fontFamily:"Inter,sans-serif"}}>{toast.msg}</span>
      <X size={11} style={{color:"rgba(128,128,128,.5)",flexShrink:0,marginTop:2}}/>
    </div>
  );
}

function ToastContainer({toasts, onDismiss, dark, t}) {
  return (
    <div style={{position:"fixed",bottom:24,right:24,zIndex:99999,
                 display:"flex",flexDirection:"column",gap:8,
                 pointerEvents:"none", alignItems:"flex-end"}}>
      {toasts.map(toast=>(
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} dark={dark} t={t}/>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════
   💬 CONFIRM DIALOG — Reemplaza window.confirm()
════════════════════════════════════════════════════ */

function ConfirmDialog({config, t, dark}) {
  if (!config) return null;
  const {title, message, confirmLabel, dangerLevel, onConfirm, onCancel} = config;
  const isDanger = dangerLevel === "high";
  const accent   = isDanger ? t.er : t.wrn;

  return (
    <div style={{position:"fixed",inset:0,zIndex:9998,display:"flex",alignItems:"center",
                 justifyContent:"center",padding:20,background:"rgba(0,0,0,.6)",
                 backdropFilter:"blur(5px)",animation:"cmsBackdropIn .2s ease"}}>
      <div style={{background:t.bg,borderRadius:18,width:"100%",maxWidth:400,overflow:"hidden",
                   boxShadow:`-8px 8px 24px ${t.sd}, 8px -8px 24px ${t.sl}, 0 0 0 1.5px ${accent}44`,
                   animation:"cmsModalDrop .25s cubic-bezier(.34,1.2,.64,1)"}}>
        <div style={{padding:"14px 18px",borderBottom:`1px solid ${accent}22`,
                     display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:34,height:34,borderRadius:10,display:"flex",alignItems:"center",
                       justifyContent:"center",background:`${accent}18`,flexShrink:0}}>
            {isDanger ? <Trash2 size={15} style={{color:accent}}/> : <AlertCircle size={15} style={{color:accent}}/>}
          </div>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:accent}}>{title||"¿Confirmar acción?"}</div>
            <div style={{fontSize:9,color:t.tx2,marginTop:1,fontFamily:"JetBrains Mono,monospace",
                         textTransform:"uppercase",letterSpacing:".06em"}}>NSL Admin · Acción irreversible</div>
          </div>
          <button onClick={onCancel} style={{marginLeft:"auto",background:"transparent",border:"none",
                                             cursor:"pointer",color:t.tx2}}><X size={14}/></button>
        </div>
        <div style={{padding:"14px 18px 18px"}}>
          <p style={{fontSize:12,color:t.tx2,lineHeight:1.65,marginBottom:16}}>{message}</p>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onCancel}
              style={{flex:1,padding:"9px",borderRadius:10,cursor:"pointer",
                      border:`1px solid ${t.tx2}33`,background:"transparent",
                      color:t.tx2,fontSize:12,fontWeight:600,fontFamily:"Inter,sans-serif"}}>
              Cancelar
            </button>
            <button onClick={onConfirm}
              style={{flex:2,padding:"9px",borderRadius:10,cursor:"pointer",
                      border:`1px solid ${accent}66`,background:`${accent}18`,
                      color:accent,fontSize:12,fontWeight:800,fontFamily:"Inter,sans-serif",
                      display:"flex",alignItems:"center",justifyContent:"center",gap:6,
                      textShadow:dark?`0 0 8px ${accent}66`:""}}>
              {isDanger ? <Trash2 size={12}/> : <Check size={12}/>}
              {confirmLabel||"Confirmar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   HELPERS
════════════════════════════════════════════════════ */

function SpinIcon({color="currentColor",size=14}) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      style={{animation:"cmsSpin .7s linear infinite",flexShrink:0}}>
      <circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2.5"
        strokeDasharray="40 20" strokeLinecap="round" opacity=".3"/>
      <path d="M12 2a10 10 0 0 1 10 10" stroke={color} strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function CopyBtn({text,t,color}) {
  const [copied,setCopied]=useState(false);
  const c=color||"#00FFFF";
  return (
    <button onClick={()=>{
      navigator.clipboard.writeText(text).catch(()=>{});
      setCopied(true);setTimeout(()=>setCopied(false),2000);
    }} style={{display:"flex",alignItems:"center",gap:4,padding:"4px 10px",
      borderRadius:7,border:"none",cursor:"pointer",
      background:copied?(t.ok+"22"):(c+"14"),color:copied?t.ok:c,
      fontSize:10,fontWeight:700,fontFamily:"Inter,sans-serif",transition:"all .2s"}}>
      {copied?<Check size={10}/>:<Copy size={10}/>}
      {copied?"Copiado":"Copiar"}
    </button>
  );
}

/* ════════════════════════════════════════════════════
   MILITARY MODAL (para guardar ejercicio individual)
════════════════════════════════════════════════════ */

function MilitaryModal({t,dark,exerciseId,exerciseTitle,onConfirm,onCancel}) {
  const [countdown,setCountdown]=useState(3);
  const [confirmed,setConfirmed]=useState(false);
  const [shaking,  setShaking]  =useState(false);
  const timerRef=useRef(null);
  useEffect(()=>{
    if(countdown===0)return;
    timerRef.current=setTimeout(()=>setCountdown(c=>c-1),1000);
    return()=>clearTimeout(timerRef.current);
  },[countdown]);
  const handleConfirm=()=>{setConfirmed(true);setTimeout(()=>onConfirm(),600);};
  const handleCancel =()=>{setShaking(true);setTimeout(()=>{setShaking(false);onCancel();},400);};
  return (
    <div className="cms-modal-backdrop" style={{position:"fixed",inset:0,zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:20,background:"rgba(0,0,0,.72)",backdropFilter:"blur(6px)"}}>
      <div className={"cms-modal-panel"+(shaking?" cms-shake":"")} style={{background:t.bg,borderRadius:20,width:"100%",maxWidth:440,overflow:"hidden",boxShadow:("-12px 12px 28px "+t.sd+",12px -12px 28px "+t.sl+",0 0 0 2px "+t.er+"44")}}>
        <div style={{background:(t.er+"18"),borderBottom:("1px solid "+t.er+"33"),padding:"14px 18px",display:"flex",alignItems:"center",gap:10}}>
          <div style={{width:36,height:36,borderRadius:10,background:(t.er+"18"),display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,border:("1px solid "+t.er+"55")}} className="cms-danger-ring"><ShieldAlert size={17} style={{color:t.er}}/></div>
          <div>
            <div style={{fontSize:13,fontWeight:800,color:t.er,letterSpacing:".02em",textShadow:dark?("0 0 12px "+t.er+"66"):""}}>ZONA DE PELIGRO · PRODUCCIÓN</div>
            <div style={{fontSize:9,color:t.tx2,marginTop:1,fontFamily:"JetBrains Mono,monospace",letterSpacing:".08em",textTransform:"uppercase"}}>NSL · Admin Override · Irreversible</div>
          </div>
          <button onClick={handleCancel} style={{marginLeft:"auto",background:"transparent",border:"none",cursor:"pointer",color:t.tx2,display:"flex",alignItems:"center",padding:4}}><X size={15}/></button>
        </div>
        <div style={{padding:"18px 20px"}}>
          <div style={{display:"flex",gap:10,padding:"12px 14px",borderRadius:12,background:(dark?(t.wrn+"0d"):(t.wrn+"08")),border:("1px solid "+t.wrn+"33"),marginBottom:14}}>
            <AlertTriangle size={16} style={{color:t.wrn,flexShrink:0,marginTop:1}}/>
            <p style={{fontSize:11,color:t.tx2,lineHeight:1.65}}>Estás a punto de <span style={{fontWeight:700,color:t.wrn}}>sobrescribir el ejercicio en producción</span>. Esta operación modifica la base de datos en vivo y no puede deshacerse automáticamente.</p>
          </div>
          <div style={{background:t.bg,borderRadius:10,padding:"10px 12px",marginBottom:14,boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl)}}>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:t.tx2,marginBottom:6}}>Objetivo de la operación</div>
            {[["Ejercicio ID","#"+exerciseId],["Título",exerciseTitle],["Operación SQL","UPDATE exercises SET data = $newData WHERE id = $id"],["Entorno","PRODUCTION · Neon.tech"],].map(([k,v])=>(
              <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",borderBottom:("1px solid "+(dark?"rgba(255,255,255,.04)":"rgba(0,0,0,.04)")),paddingTop:4,paddingBottom:4}}>
                <span style={{fontSize:10,color:t.tx2,flexShrink:0,paddingRight:8}}>{k}</span>
                <span style={{fontSize:9,fontWeight:600,color:t.tx,textAlign:"right",fontFamily:"JetBrains Mono,monospace",maxWidth:220,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:k==="Operación SQL"?"normal":"nowrap"}}>{v}</span>
              </div>
            ))}
          </div>
          {countdown>0&&(<div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",borderRadius:10,background:dark?"rgba(255,255,255,.03)":"rgba(0,0,0,.03)",marginBottom:14}}><SpinIcon color={t.wrn} size={12}/><span style={{fontSize:10,color:t.tx2}}>El botón se activa en <span style={{fontWeight:800,color:t.wrn,fontFamily:"JetBrains Mono,monospace"}}>{countdown}s</span></span></div>)}
          <div style={{display:"flex",gap:8}}>
            <button onClick={handleCancel} style={{flex:1,padding:"10px",borderRadius:11,cursor:"pointer",border:("1px solid "+t.tx2+"33"),background:"transparent",color:t.tx2,fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif"}}>Cancelar</button>
            <button onClick={handleConfirm} disabled={countdown>0||confirmed} style={{flex:2,padding:"10px",borderRadius:11,cursor:countdown>0?"not-allowed":"pointer",border:("1px solid "+(countdown>0?(t.tx2+"33"):(t.er+"66"))),background:confirmed?(t.ok+"14"):(countdown>0?"transparent":(t.er+"14")),color:confirmed?t.ok:(countdown>0?t.tx2:t.er),fontSize:12,fontWeight:800,fontFamily:"Inter,sans-serif",opacity:countdown>0?.4:1,transition:"all .3s",display:"flex",alignItems:"center",justifyContent:"center",gap:7,textShadow:dark&&countdown===0&&!confirmed?("0 0 8px "+t.er+"55"):""}}>{confirmed?<><Check size={13}/> Ejecutando…</>:<><ShieldAlert size={13}/> Confirmar Modificación</>}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   EXERCISE CARD
════════════════════════════════════════════════════ */

function ExerciseCard({ex,t,dark,HICOLOR,onSaveRequest}) {
  const [expanded,  setExpanded]  = useState(false);
  const [editMode,  setEditMode]  = useState(false);
  const [jsonDraft, setJsonDraft] = useState("");
  const [jsonError, setJsonError] = useState(null);
  const [showRaw,   setShowRaw]   = useState(false);
  const [hr,        setHr]        = useState(false);
  const meta = EX_TYPE_META[ex.exercise_type]||{color:HICOLOR,label:ex.exercise_type,emoji:"📄"};

  const openEdit=()=>{setJsonDraft(JSON.stringify(ex.data,null,2));setJsonError(null);setEditMode(true);setExpanded(true);};
  const validateDraft=(text)=>{try{JSON.parse(text);setJsonError(null);return true;}catch(e){setJsonError(e.message);return false;}};
  const handleSaveClick=()=>{if(!validateDraft(jsonDraft))return;onSaveRequest(ex.id,ex.data?.title||"Sin título",jsonDraft);};
  const handleClose=()=>{setEditMode(false);setExpanded(false);setJsonError(null);};

  return (
    <div onMouseEnter={()=>setHr(true)} onMouseLeave={()=>setHr(false)} style={{borderRadius:14,overflow:"hidden",transition:"all .2s",background:t.bg,boxShadow:expanded?("-6px 6px 12px "+t.sd+",6px -6px 12px "+t.sl+",0 0 0 1.5px "+meta.color+"55"):hr?("-3px 3px 8px "+t.sd+",3px -3px 8px "+t.sl+",0 0 0 1px "+meta.color+"22"):("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl),border:("1px solid "+meta.color+"18")}}>
      <div style={{display:"flex",alignItems:"center",gap:10,padding:"11px 13px",cursor:"pointer",background:expanded?(dark?(meta.color+"0a"):(meta.color+"06")):"transparent",borderBottom:expanded?("1px solid "+(dark?"rgba(255,255,255,.05)":"rgba(0,0,0,.05)")):"none"}} onClick={()=>{if(!editMode)setExpanded(o=>!o);}}>
        <div style={{width:34,height:34,borderRadius:10,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,background:(meta.color+"14"),boxShadow:("0 0 0 1px "+meta.color+"28")}}>{meta.emoji}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{fontSize:12,fontWeight:600,color:t.tx,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",lineHeight:1.3,marginBottom:3}}>{(ex.data&&ex.data.title)||"Sin título"}</div>
          <div style={{display:"flex",gap:5,alignItems:"center",flexWrap:"wrap"}}>
            <div style={{width:18,height:18,borderRadius:5,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,flexShrink:0,background:(meta.color+"18"),color:meta.color,fontFamily:"JetBrains Mono,monospace"}}>{ex.position}</div>
            <span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:50,background:(meta.color+"14"),color:meta.color}}>{meta.label}</span>
            {ex.data&&ex.data.xp>0&&(<span style={{fontSize:8,fontWeight:700,padding:"2px 6px",borderRadius:50,background:(HICOLOR+"14"),color:HICOLOR,fontFamily:"JetBrains Mono,monospace"}}>{ex.data.xp} XP</span>)}
          </div>
        </div>
        <div style={{display:"flex",gap:5,flexShrink:0}} onClick={e=>e.stopPropagation()}>
          <button onClick={openEdit} style={{display:"flex",alignItems:"center",gap:4,padding:"5px 10px",borderRadius:8,border:("1px solid "+meta.color+"44"),cursor:"pointer",background:(meta.color+"0d"),color:meta.color,fontSize:10,fontWeight:700,fontFamily:"Inter,sans-serif",transition:"all .18s"}}><Pencil size={10}/> Editar</button>
          <button onClick={()=>setExpanded(o=>!o)} style={{width:28,height:28,borderRadius:8,border:"none",cursor:"pointer",background:"transparent",color:t.tx2,display:"flex",alignItems:"center",justifyContent:"center"}}>{expanded?<ChevronUp size={13}/>:<ChevronDown size={13}/>}</button>
        </div>
      </div>
      {expanded&&(
        <div className="cms-card-expand" style={{padding:"12px 14px",display:"flex",flexDirection:"column",gap:10}}>
          {!editMode?(
            <div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:6}}>
                <span style={{fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:t.tx2}}>data preview</span>
                <button onClick={()=>setShowRaw(o=>!o)} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:6,border:"none",cursor:"pointer",background:(HICOLOR+"10"),color:HICOLOR,fontSize:9,fontWeight:600}}>{showRaw?<EyeOff size={9}/>:<Eye size={9}/>} {showRaw?"Ocultar":"Ver JSON"}</button>
              </div>
              {showRaw?(
                <div className="cms-code-editor" style={{borderRadius:10,overflow:"hidden",boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl)}}>
                  <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",background:dark?"#0d0d1a":"#1e1e2e",borderBottom:"1px solid rgba(255,255,255,.05)"}}>
                    {["#ff5f57","#febc2e","#28c840"].map(c=>(<div key={c} style={{width:8,height:8,borderRadius:"50%",background:c}}/>))}
                    <span style={{fontSize:8,fontFamily:"JetBrains Mono,monospace",color:"rgba(255,255,255,.25)",marginLeft:6}}>exercise_{ex.id}.json</span>
                    <div style={{marginLeft:"auto"}}><CopyBtn text={JSON.stringify(ex.data,null,2)} t={t} color={meta.color}/></div>
                  </div>
                  <pre style={{margin:0,padding:"10px 12px",maxHeight:180,overflowY:"auto",fontSize:10,lineHeight:1.65,fontFamily:"JetBrains Mono,monospace",color:dark?"#a3e635":"#4ade80",background:dark?"#0d1117":"#1e1e2e",whiteSpace:"pre-wrap"}}>{JSON.stringify(ex.data,null,2)}</pre>
                </div>
              ):(
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {Object.entries(ex.data).filter(([k])=>!["points","questions","blanks","pairs","matches","solution","words_or_sentences","visual_gallery_cards","learning_objectives"].includes(k)).slice(0,6).map(([k,v])=>(
                    <div key={k} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"4px 8px",borderRadius:7,background:dark?"rgba(255,255,255,.02)":"rgba(0,0,0,.02)"}}>
                      <span style={{fontSize:9,color:t.tx2,fontFamily:"JetBrains Mono,monospace"}}>{k}</span>
                      <span style={{fontSize:9,fontWeight:600,color:t.tx,maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",textAlign:"right",fontFamily:"JetBrains Mono,monospace"}}>{typeof v==="object"?("["+(Array.isArray(v)?v.length+" items":"object")+"]"):String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                <span style={{fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:meta.color}}>Modo Edicion · data.json</span>
                <button onClick={handleClose} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px",borderRadius:6,border:"none",cursor:"pointer",background:"transparent",color:t.tx2,fontSize:9,fontWeight:600}}><X size={9}/> Cerrar</button>
              </div>
              <div className="cms-code-editor" style={{borderRadius:10,overflow:"hidden",boxShadow:jsonError?("0 0 0 2px "+t.er+"55"):("0 0 0 1.5px "+meta.color+"44")}}>
                <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",background:dark?"#0d0d1a":"#1e1e2e",borderBottom:"1px solid rgba(255,255,255,.05)"}}>
                  {["#ff5f57","#febc2e","#28c840"].map(c=>(<div key={c} style={{width:8,height:8,borderRadius:"50%",background:c}}/>))}
                  <span style={{fontSize:8,fontFamily:"JetBrains Mono,monospace",color:"rgba(255,255,255,.25)",marginLeft:6}}>exercise_{ex.id} · edit mode</span>
                  {jsonDraft&&<div style={{marginLeft:"auto"}}><CopyBtn text={jsonDraft} t={t} color={meta.color}/></div>}
                </div>
                <textarea value={jsonDraft} onChange={e=>{setJsonDraft(e.target.value);validateDraft(e.target.value);}} spellCheck={false} rows={14} style={{width:"100%",background:dark?"#0d1117":"#1e1e2e",color:jsonError?(dark?"#ff6b6b":"#ef4444"):(dark?"#00ff88":"#4ade80"),border:"none",outline:"none",padding:"10px 12px",fontSize:10.5,lineHeight:1.7,fontFamily:"JetBrains Mono,monospace",resize:"vertical"}}/>
              </div>
              {jsonError&&(<div style={{display:"flex",gap:6,padding:"6px 10px",borderRadius:8,background:(t.er+"0e"),border:("1px solid "+t.er+"33")}}><XCircle size={11} style={{color:t.er,flexShrink:0,marginTop:1}}/><span style={{fontSize:9,color:t.er,fontFamily:"JetBrains Mono,monospace",lineHeight:1.5}}>{jsonError}</span></div>)}
              <button onClick={handleSaveClick} disabled={!!jsonError} style={{padding:"10px",borderRadius:11,cursor:jsonError?"not-allowed":"pointer",border:("1px solid "+(jsonError?(t.tx2+"33"):(t.er+"55"))),background:jsonError?"transparent":(t.er+"14"),color:jsonError?t.tx2:t.er,fontSize:12,fontWeight:800,fontFamily:"Inter,sans-serif",opacity:jsonError?.4:1,transition:"all .2s",display:"flex",alignItems:"center",justifyContent:"center",gap:7,textShadow:dark&&!jsonError?("0 0 8px "+t.er+"55"):""}}><Save size={12}/> Guardar Cambios en Produccion</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════════════════
   ⚙️ MotorCard — el motor de la clase (sustituye al espejo de Spacy)
════════════════════════════════════════════════════ */
// ===================================================================
// ===================================================================

function MotorCard({ lessonId, t, dark, HICOLOR }) {
  const [motores,   setMotores]   = useState([]);
  const [actual,    setActual]    = useState(null);
  const [cargando,  setCargando]  = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [abierto,   setAbierto]   = useState(false);

  const token = localStorage.getItem('token');

  // El catálogo sale del servidor, de `MOTOR_DEFINITIONS`. Si esta pantalla
  // tuviera su propia lista, el día que se añada un motor habría que acordarse
  // de dos sitios — y el que se olvidaría es éste.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [rm, rl] = await Promise.all([
          fetch('/api/admin/exercises/motores', {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`/api/engines/motor-info/${lessonId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        if (!vivo) return;
        if (rm.ok) setMotores((await rm.json()).motores || []);
        if (rl.ok) setActual(await rl.json());
      } catch { /* la tarjeta lo dice abajo */ }
      finally { if (vivo) setCargando(false); }
    })();
    return () => { vivo = false; };
  }, [lessonId]);

  const cambiar = async (motor) => {
    if (guardando) return;
    setGuardando(true);
    try {
      const res = await fetch(
        `/api/admin/exercises/lessons/${lessonId}/motor`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ motor_type: motor }),
        },
      );
      const data = await res.json();
      if (res.ok) {
        nslToast.success(data.message || 'Motor cambiado');
        const r = await fetch(`/api/engines/motor-info/${lessonId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.ok) setActual(await r.json());
        setAbierto(false);
      } else {
        nslToast.error(
          typeof data.detail === 'string' ? data.detail : 'No se pudo cambiar',
        );
      }
    } catch { nslToast.error('Error de red al cambiar el motor'); }
    finally { setGuardando(false); }
  };

  // `error` viene cuando el motor guardado no existe en el generador. Pasó de
  // verdad: cuatro lecciones tenían `mixed_review`, que nunca se definió, y
  // devolvían CERO bloques — la clase se abría y no había nada que hacer.
  const roto = Boolean(actual?.error);
  const bloques = actual?.total_blocks ?? 0;

  const caja = {
    borderRadius: 13,
    border: `1px solid ${roto ? t.er + '55' : HICOLOR + '33'}`,
    background: dark ? (roto ? t.er + '0c' : HICOLOR + '08') : '#00000004',
    padding: '14px 15px',
  };

  if (cargando) {
    return <div style={{ ...caja, fontSize: 11, color: t.tx2 }}>Cargando el motor…</div>;
  }

  return (
    <div style={caja}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 15 }}>{roto ? '⚠️' : '⚙️'}</span>
        <div style={{ flex: 1, minWidth: 150 }}>
          <div style={{
            fontSize: 12, fontWeight: 800,
            color: roto ? t.er : (dark ? '#fff' : '#111'),
            fontFamily: 'Inter,sans-serif',
          }}>
            {actual?.motor_type || 'Sin motor asignado'}
          </div>
          <div style={{ fontSize: 10, color: t.tx2, marginTop: 2 }}>
            {roto
              ? 'Este motor no existe en el generador: la clase se abre sin un solo bloque'
              : `${bloques} bloques · el alumno los recorre en este orden`}
          </div>
        </div>
        <button
          onClick={() => setAbierto((v) => !v)}
          disabled={guardando}
          style={{
            padding: '6px 13px', borderRadius: 9, cursor: 'pointer',
            border: `1px solid ${HICOLOR}44`,
            background: dark ? HICOLOR + '12' : HICOLOR + '0a',
            color: HICOLOR, fontSize: 11, fontWeight: 700,
            fontFamily: 'Inter,sans-serif',
          }}
        >
          {guardando ? 'Guardando…' : abierto ? 'Cerrar' : 'Cambiar motor'}
        </button>
      </div>

      {/* Los bloques del motor actual: elegir un motor es elegir la
          estructura pedagógica entera de la clase, así que se enseña. */}
      {!abierto && !roto && (actual?.blocks || []).length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 11,
        }}>
          {actual.blocks.map((b) => (
            <span key={b.num} style={{
              fontSize: 9, padding: '3px 8px', borderRadius: 50,
              background: dark ? '#ffffff0a' : '#00000006',
              border: '1px solid ' + (dark ? '#ffffff14' : '#00000010'),
              color: t.tx2,
            }}>
              {b.num + 1}. {b.label}
            </span>
          ))}
        </div>
      )}

      {abierto && (
        <div style={{ marginTop: 12, display: 'grid', gap: 7 }}>
          {motores.map((m) => {
            const esActual = m.id === actual?.motor_type;
            return (
              <button
                key={m.id}
                onClick={() => cambiar(m.id)}
                disabled={guardando || esActual}
                style={{
                  textAlign: 'left', padding: '9px 12px', borderRadius: 10,
                  cursor: esActual ? 'default' : 'pointer',
                  border: `1px solid ${esActual ? HICOLOR + '66' : (dark ? '#ffffff14' : '#00000012')}`,
                  background: esActual
                    ? (dark ? HICOLOR + '14' : HICOLOR + '0a')
                    : 'transparent',
                  color: dark ? '#fff' : '#111',
                  fontFamily: 'Inter,sans-serif',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 800 }}>
                  {m.id} {esActual && <span style={{ color: HICOLOR }}>· actual</span>}
                </div>
                <div style={{ fontSize: 9, color: t.tx2, marginTop: 3 }}>
                  {m.bloques} bloques · {m.etiquetas.slice(0, 3).join(' → ')}…
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


/* ════════════════════════════════════════════════════
   LESSON PREVIEW (con MotorCard)
════════════════════════════════════════════════════ */

/* ════════════════════════════════════════════════════
   LESSON PREVIEW (con MotorCard y LessonAudit)
════════════════════════════════════════════════════ */

function LessonPreview({lesson,t,dark,HICOLOR,levelColor,onInject,onDelete}) {
  const [modalState,setModalState]=useState(null);
  const [savedIds,  setSavedIds] =useState([]);
  const [exercises, setExercises]=useState([]);
  const [loading, setLoading]=useState(true);

  useEffect(() => {
    if(!lesson) return;
    setLoading(true);
    const token = localStorage.getItem('token');
    fetch(`/api/course/lesson/${lesson.id}`, { headers: { 'Authorization': `Bearer ${token}` } })
    .then(res => res.ok ? res.json() : null)
    .then(data => {
      if (data && data.exercises) {
        setExercises(data.exercises.sort((a,b) => a.position - b.position));
      } else { setExercises([]); }
    })
    .catch(err => console.error(err))
    .finally(() => setLoading(false));
  }, [lesson]);

  const handleSaveRequest=(exId,exTitle,draft)=>{setModalState({exerciseId:exId,exerciseTitle:exTitle,draft});};
  const handleConfirm= async ()=>{
    try {
      const token = localStorage.getItem('token');
      const payload = { data: JSON.parse(modalState.draft) };
      const res = await fetch(`/api/admin/exercises/${modalState.exerciseId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (res.ok) {
        setSavedIds(p=>[...p,modalState.exerciseId]);
        setExercises(prev => prev.map(ex => ex.id === modalState.exerciseId ? { ...ex, data: payload.data } : ex));
        nslToast.success(`Ejercicio #${modalState.exerciseId} guardado en producción ✓`);
      } else {
        nslToast.error("Error al actualizar la base de datos.");
      }
    } catch(e) { console.error(e); nslToast.error("Error de red al guardar."); }
    setModalState(null);
  };
  const handleCancelModal=()=>setModalState(null);

  if(!lesson)return null;

  return (
    <>
      {modalState&&(<MilitaryModal t={t} dark={dark} exerciseId={modalState.exerciseId} exerciseTitle={modalState.exerciseTitle} onConfirm={handleConfirm} onCancel={handleCancelModal}/>)}
      <div style={{display:"flex",flexDirection:"column",gap:14,animation:"cmsFadeUp .25s ease",height:"100%"}}>
        <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
          <div style={{width:48,height:48,borderRadius:14,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,background:t.bg,boxShadow:("-6px 6px 12px "+t.sd+",6px -6px 12px "+t.sl+",0 0 0 2px "+levelColor+"44")}}>📗</div>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:700,color:t.tx,marginBottom:5}}>{lesson.title}</div>
            <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
              {[{v:"Pos. "+lesson.pos, c:levelColor},{v:exercises.length+" ejercicios", c:t.ok},{v:lesson.xp+" XP", c:HICOLOR, mono:true},{v:"id: "+lesson.id, c:t.tx2, mono:true},].map((b,i)=>(<span key={i} style={{fontSize:9,fontWeight:700,padding:"2px 7px",borderRadius:50,background:(b.c+"14"),color:b.c,fontFamily:b.mono?"JetBrains Mono,monospace":"Inter,sans-serif"}}>{b.v}</span>))}
            </div>
          </div>
          <button onClick={onDelete}
            style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:10,
                    border:`1px solid ${t.er}44`,background:dark?`${t.er}1a`:`${t.er}14`,
                    color:t.er,fontSize:11,fontWeight:700,cursor:"pointer",
                    fontFamily:"Inter,sans-serif",transition:"all .2s"}}>
            <Trash2 size={14}/> Borrar Lección
          </button>
        </div>

        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:t.tx2}}>Ejercicios Reales</span>
            <span style={{fontSize:9,padding:"1px 6px",borderRadius:50,background:(HICOLOR+"14"),color:HICOLOR,fontFamily:"JetBrains Mono,monospace",fontWeight:700}}>{exercises.length}</span>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:5,padding:"3px 8px",borderRadius:50,background:t.bg,boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl)}}>
            <div style={{width:5,height:5,borderRadius:"50%",background:t.ok,animation:"cmsPls 2s ease-in-out infinite"}}/>
            <span style={{fontSize:8,fontWeight:700,color:t.ok,fontFamily:"JetBrains Mono,monospace"}}>Neon DB Live</span>
          </div>
        </div>

        <div style={{display:"flex",flexDirection:"column",gap:8,overflowY:"auto",maxHeight:520,paddingRight:2}}>
          {loading ? (
             <div style={{padding:"20px",textAlign:"center",color:t.tx2,fontSize:12}}><SpinIcon color={HICOLOR} size={18}/> Extrayendo de Neon.tech...</div>
          ) : exercises.length === 0 ? (
             <div style={{padding:"20px",textAlign:"center",color:t.tx2,fontSize:12,background:t.bg,borderRadius:12,boxShadow:t.ixs}}>Esta lección aún no tiene ejercicios inyectados.</div>
          ) : (
            exercises.map(ex=>(
              <div key={ex.id} style={{position:"relative"}}>
                {savedIds.includes(ex.id)&&(<div className="cms-success-pop" style={{position:"absolute",top:-6,right:-4,zIndex:10,display:"flex",alignItems:"center",gap:3,padding:"2px 7px",borderRadius:50,background:dark?(t.ok+"22"):(t.ok+"18"),border:("1px solid "+t.ok+"44"),boxShadow:dark?("0 0 8px "+t.ok+"44"):""}}><Check size={9} style={{color:t.ok}}/><span style={{fontSize:8,fontWeight:800,color:t.ok}}>Guardado</span></div>)}
                <ExerciseCard ex={ex} t={t} dark={dark} HICOLOR={HICOLOR} onSaveRequest={handleSaveRequest}/>
              </div>
            ))
          )}

          {/* ── EL MOTOR DE LA CLASE ──────────────────────────────────
              Aquí estaba el espejo de Spacy: una tarjeta que mantenía una
              tabla de 4 filas que nadie leía. En su lugar va lo que de verdad
              decide cómo se da la clase — el motor, y con él la estructura
              pedagógica entera. Ver la lápida en `admin_routes.py`. */}
          <div style={{ position: "relative" }}>
            <div style={{
              position: "absolute", top: -8, left: 10, zIndex: 10,
              display: "flex", alignItems: "center", gap: 4,
              padding: "1px 8px", borderRadius: 50,
              background: dark ? "#00FFFF18" : "#00FFFF12",
              border: "1px solid #00FFFF33"
            }}>
              <span style={{ fontSize: 8, fontWeight: 800, color: "#00FFFF", fontFamily: "JetBrains Mono,monospace" }}>⚙</span>
              <span style={{ fontSize: 7, color: "#00FFFF" }}>MOTOR DE LA CLASE</span>
            </div>
            <MotorCard lessonId={lesson.id} t={t} dark={dark} HICOLOR={HICOLOR} />
          </div>
        </div>

        {/* ── AUDITOR DE EJERCICIOS (NUEVO) ── */}
        <LessonAudit exercises={exercises} t={t} dark={dark} HICOLOR={HICOLOR} />

        <button onClick={onInject} style={{padding:"10px",borderRadius:11,cursor:"pointer",flexShrink:0,border:("1px solid "+t.er+"44"),background:dark?(t.er+"0e"):(t.er+"08"),color:t.er,fontSize:12,fontWeight:700,fontFamily:"Inter,sans-serif",display:"flex",alignItems:"center",justifyContent:"center",gap:6,transition:"all .2s"}}><Plus size={12}/> Inyectar nuevos ejercicios</button>
      </div>
    </>
  );
}

/* ════════════════════════════════════════════════════
   SCREEN COMPONENTS (VALIDATING, VALIDATION RESULT, SEEDING, SEEDED)
════════════════════════════════════════════════════ */

function ValidatingScreen({t,HICOLOR}) {
  return (<div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"32px 0",gap:16,animation:"cmsFadeUp .25s ease"}}><SpinIcon color={HICOLOR} size={24}/><div style={{fontSize:14,fontWeight:700,color:t.tx}}>Validando Safe Seeding...</div></div>);
}

function buildChecks(v) {
  const c = [];
  c.push({ok:v.syntaxOk, msg:"Sintaxis JSON válida"});
  if (!v.syntaxOk) { c.push({ok:false, msg:"Error de sintaxis: "+v.error}); return c; }

  c.push({ok:v.countOk,  msg: v.countOk ? "10 ejercicios exactos ✓" : `Recuento incorrecto: ${v.count}/10 ejercicios (se requieren exactamente 10)`});
  c.push({ok:v.lessonIdOk, msg: v.lessonIdOk ? `lesson_id único: ${v.lessonIds[0]}` : `Múltiples lesson_id detectados: ${v.lessonIds.join(", ")}`});
  c.push({ok:v.posOk, msg: v.posOk ? `Posiciones 1-${v.count} sin duplicados` : "Posiciones duplicadas o desordenadas"});
  c.push({ok:v.allHaveType&&v.allHaveData, msg: v.allHaveType&&v.allHaveData ? "Estructura raíz correcta (type, lesson_id, position, data)" : "Faltan campos raíz en algún ejercicio"});

  // ── Sequence validation ──────────────────────────────
  const seqOk = v.parsed && CANONICAL_SEQUENCE.every((t,i) => v.parsed[i]?.exercise_type === t);
  c.push({ok:!!seqOk, msg: seqOk
    ? "Secuencia de tipos canónica ✓ (spacy→notes→quiz→fill→match→listen→pronun→error→reading→writing)"
    : "⚠️ Secuencia incorrecta — revisa el orden de exercise_type"});

  // ── Deep structural errors ───────────────────────────
  if (v.deepErrors && v.deepErrors.length > 0) {
    v.deepErrors.slice(0, 8).forEach(err => c.push({ok:false, msg:err}));
    if (v.deepErrors.length > 8)
      c.push({ok:false, msg:`... y ${v.deepErrors.length - 8} errores más`});
  } else if (v.syntaxOk && v.countOk) {
    c.push({ok:true, msg:"Estructura interna de cada ejercicio válida ✓"});
  }

  if (v.hasSpacy)   c.push({ok:true, msg:"Tutor IA (Spacy) configurado"});
  if (v.totalXP>0)  c.push({ok:true, msg:`${v.totalXP} XP total`});
  if (v.hasAudio)   c.push({ok:true, msg:"Audio URL detectado (Listening)"});
  return c;
}

function ValidationResult({t,dark,HICOLOR,validation,onSeed,onReset}) {
  const [seedHov,setSeedHov]=useState(false);
  const checks = buildChecks(validation);
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12,animation:"cmsFadeUp .3s ease"}}>
      <div style={{background:t.bg,borderRadius:14,padding:"12px 14px",boxShadow:validation.ok?("inset -3px 3px 8px "+t.sd+",inset 3px -3px 8px "+t.sl+",0 0 0 1px "+t.ok+"33"):("inset -3px 3px 8px "+t.sd+",inset 3px -3px 8px "+t.sl+",0 0 0 1px "+t.er+"33")}}>
        <div style={{fontSize:12,fontWeight:800,marginBottom:10,color:validation.ok?t.ok:t.er,textShadow:dark&&validation.ok?("0 0 10px "+t.ok+"55"):""}}>{validation.ok?"✅ ADN Válido · Safe Seeding Activo":"❌ Validación fallida — corrige los errores"}</div>
        <div style={{display:"flex",flexDirection:"column",gap:5}}>{checks.map((chk,i)=>(<div key={i} style={{display:"flex",gap:8,padding:"6px 8px",borderRadius:9,background:t.bg,boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl)}}>{chk.ok?<CheckCircle2 size={13} style={{color:t.ok,flexShrink:0}}/>:<XCircle size={13} style={{color:t.er,flexShrink:0}}/>}<span style={{fontSize:11,fontWeight:600,color:chk.ok?t.tx:t.er,fontFamily:chk.ok?"Inter,sans-serif":"JetBrains Mono,monospace"}}>{chk.msg}</span></div>))}</div>
      </div>
      {validation.ok&&(<div style={{padding:"10px 12px",borderRadius:12,background:t.bg,boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl)}}><div style={{fontSize:9,fontWeight:700,letterSpacing:".1em",textTransform:"uppercase",color:t.tx2,marginBottom:6}}>SQL · Bulk Insert (Safe Seeding)</div><div style={{fontFamily:"JetBrains Mono,monospace",fontSize:10,color:dark?"#a3e635":"#16a34a",lineHeight:1.7}}>INSERT INTO exercises (lesson_id, exercise_type, position, data)<br/>VALUES <span style={{color:t.tx2}}>({validation.count} rows — {validation.totalXP} XP total)</span></div></div>)}
      <div style={{display:"flex",gap:8}}>
        <button onClick={onReset} style={{flex:1,padding:"9px",borderRadius:11,border:("1px solid "+t.tx2+"33"),background:"transparent",color:t.tx2,fontWeight:600,cursor:"pointer",fontFamily:"Inter,sans-serif",fontSize:12}}>Editar JSON</button>
        {validation.ok&&(<button onMouseEnter={()=>setSeedHov(true)} onMouseLeave={()=>setSeedHov(false)} onClick={onSeed} style={{flex:2,padding:"9px",borderRadius:11,border:("1px solid "+t.ok+"55"),cursor:"pointer",background:seedHov?(t.ok+"1a"):(t.ok+"0e"),color:t.ok,fontWeight:800,fontFamily:"Inter,sans-serif",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:7,textShadow:dark?("0 0 8px "+t.ok+"55"):"",transition:"all .2s"}}><Database size={13}/> Ejecutar Bulk Insert</button>)}
      </div>
    </div>
  );
}

function SeedingScreen({t,dark,HICOLOR,step,steps}) {
  return (
    <div style={{display:"flex",flexDirection:"column",gap:12,animation:"cmsFadeUp .25s ease"}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}><SpinIcon color={t.ok} size={16}/><span style={{fontSize:13,fontWeight:700,color:t.ok,textShadow:dark?("0 0 10px "+t.ok+"55"):""}}>Sembrando en Neon.tech...</span></div>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>{steps.map((s,i)=>(<div key={i} style={{display:"flex",gap:8,padding:"7px 10px",borderRadius:10,background:t.bg,boxShadow:("inset -2px 2px 5px "+t.sd+",inset 2px -2px 5px "+t.sl),opacity:step>=i?1:.3,transition:"opacity .3s"}}>{step>i?<CheckCircle2 size={13} style={{color:t.ok,flexShrink:0}}/>:step===i?<SpinIcon color={HICOLOR} size={13}/>:<div style={{width:13,height:13,borderRadius:"50%",flexShrink:0,background:dark?"rgba(255,255,255,.06)":"rgba(0,0,0,.06)"}}/>}<span style={{fontSize:11,fontFamily:step===i?"JetBrains Mono,monospace":"Inter,sans-serif",color:step>=i?t.tx:t.tx2}}>{s}</span></div>))}</div>
      <div style={{height:3,borderRadius:2,overflow:"hidden",background:dark?"rgba(255,255,255,.06)":"rgba(0,0,0,.06)"}}><div style={{height:"100%",borderRadius:2,background:("linear-gradient(90deg,"+HICOLOR+","+t.ok+")"),width:((step/(steps.length-1))*100)+"%",transition:"width .45s cubic-bezier(.4,0,.2,1)",boxShadow:dark?("0 0 8px "+t.ok+"88"):""}}/></div>
    </div>
  );
}

function SeededScreen({t,dark,HICOLOR,validation,onNew,onBack}) {
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",padding:"24px 0",gap:16,animation:"cmsFadeUp .3s ease"}}>
      <div className="cms-success-pop" style={{width:64,height:64,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",background:dark?(t.ok+"18"):(t.ok+"12"),boxShadow:dark?("0 0 32px "+t.ok+"44"):undefined}}><CheckCircle2 size={30} style={{color:t.ok}}/></div>
      <div style={{textAlign:"center"}}>
        <div style={{fontSize:16,fontWeight:800,color:t.ok,marginBottom:4,textShadow:dark?("0 0 14px "+t.ok+"77"):""}}>{validation&&validation.count} ejercicios sembrados!</div>
        {validation&&validation.totalXP>0&&(<div style={{fontSize:11,color:t.tx2}}>+<span style={{fontWeight:700,color:HICOLOR,fontFamily:"JetBrains Mono,monospace"}}>{validation.totalXP} XP</span> añadidos</div>)}
      </div>
      <div style={{display:"flex",gap:8,width:"100%",maxWidth:300}}>
        <button onClick={onBack} style={{flex:1,padding:"9px",borderRadius:11,border:("1px solid "+t.tx2+"33"),background:"transparent",color:t.tx2,fontWeight:600,cursor:"pointer",fontFamily:"Inter,sans-serif",fontSize:12}}>Volver</button>
        <button onClick={onNew} style={{flex:2,padding:"9px",borderRadius:11,border:("1px solid "+HICOLOR+"44"),background:(HICOLOR+"12"),color:HICOLOR,fontWeight:700,cursor:"pointer",fontFamily:"Inter,sans-serif",fontSize:12,display:"flex",alignItems:"center",justifyContent:"center",gap:6}}><Plus size={12}/> Nueva inyección</button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   MAIN COMPONENT (CourseCmsTab)
════════════════════════════════════════════════════ */

export default function CourseCmsTab({t,HICOLOR,dark,bp}) {
  const [activeLevel, setActiveLevel] = useState("A1");
  const [selLessonId, setSelLessonId] = useState(null);
  const [hovLessonId, setHovLessonId] = useState(null);
  const [mode,        setMode]        = useState("idle");
  const [inputMode,   setInputMode]   = useState("drop");
  const [jsonText,    setJsonText]    = useState("");
  const [dragOver,    setDragOver]    = useState(false);
  const [droppedFile, setDroppedFile] = useState(null);
  const [validation,  setValidation]  = useState(null);
  const [valStep, setValStep] = useState(-1); 
  const [seedStep,    setSeedStep]    = useState(0);
  const [codeFocus,   setCodeFocus]   = useState(false);
  const [showPrompt,  setShowPrompt]  = useState(false);
  const [lessonTopic, setLessonTopic] = useState("");
  const [newLessonTitle, setNewLessonTitle] = useState("");
  const [isCreatingLesson, setIsCreatingLesson] = useState(false);
  const [lessons, setLessons] = useState({});
  const [dragLessonId,     setDragLessonId]     = useState(null);
  const [dragOverLessonId, setDragOverLessonId] = useState(null);

  // ── Toast system ─────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  useEffect(()=>{ _toastSetter = setToasts; return()=>{ _toastSetter = null; }; }, []);
  const dismissToast = useCallback((id)=>{
    setToasts(p=>p.filter(t=>t.id!==id));
  }, []);

  // ── Confirm dialog ───────────────────────────────────
  const [confirmCfg, setConfirmCfg] = useState(null);
  const nslConfirm = useCallback((title, message, onConfirm, opts={}) => {
    setConfirmCfg({title, message, onConfirm, onCancel:()=>setConfirmCfg(null),
                   confirmLabel: opts.confirmLabel || "Confirmar",
                   dangerLevel:  opts.dangerLevel  || "normal"});
  }, []);

  const fetchTree = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      // ── EL ARBOL DEL CMS, NO EL DEL ALUMNO ─────────────────────────────
      // Esto leia `/api/levels`, que es el arbol del curso del alumno y
      // devuelve `id`, `title`, `number` y los campos del motor. La pantalla
      // pedia `exercises_count` y `total_xp`, que ESE endpoint no manda, asi
      // que enseñaba **0 y 0** para las 138 lecciones. Y el estado estaba
      // escrito a mano —`status: "published"`— para todas, incluida una
      // recien creada cuyos diez ejercicios dicen «Pending — Add...».
      const res = await fetch('/api/admin/exercises/arbol', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const realData = {};
        (data.niveles || []).forEach((level) => {
          realData[level.level_code] = (level.lessons || []).map((l, index) => ({
            id: l.id,
            pos: l.number || index + 1,
            title: l.title,
            // Los tres, calculados en el servidor. El estado sale de lo que la
            // leccion tiene dentro: vacia, borrador (solo andamiaje) o
            // publicada.
            exercises: l.ejercicios,
            andamiaje: l.andamiaje,
            status: l.estado === "publicada" ? "published"
                  : l.estado === "borrador"  ? "draft"
                  : "empty",
            motor: l.motor_type,
            motorOk: l.motor_ok,
          }));
        });
        setLessons(realData);
      }
    } catch(e) { console.error(e); }
  }, []);

  useEffect(() => { fetchTree(); }, [fetchTree]);

  const fileRef  = useRef(null);
  const timerRef = useRef(null);
  const levelColor = (LEVELS.find(l=>l.id===activeLevel)||LEVELS[0]).color;
  const isMobile   = bp&&bp.isMobile;

  // Los tres estados estaban definidos y solo se usaba `published`, escrito a
  // mano. Ahora los tres llegan calculados del servidor.
  const STATUS_CFG = {
    published:{color:t.ok,  label:"Publicada",         dot:true },
    draft:    {color:t.wrn, label:"Solo andamiaje",    dot:true },
    empty:    {color:t.tx2, label:"Sin ejercicios",    dot:false},
  };

  const currentLessonId = selLessonId || "[ID_ASIGNADO_AL_CREAR]";

  /* ════════════════════════════════════════════════════
     🤖 MASTER PROMPT — Anti-Alucinaciones v4
     Contrato Inquebrantable · Cambridge Quality
  ════════════════════════════════════════════════════ */
  const spacyPrompt = `Eres un Diseñador Instruccional Senior certificado por Cambridge English (CELTA/DELTA), especialista en EFL y JSON estructurado.
Tu ÚNICA salida permitida es un array JSON de EXACTAMENTE 10 objetos. Cero texto antes o después. Sin markdown, sin bloques de código.

══════════════════════════════════════════════════════
🔒 CONTRATO INQUEBRANTABLE — LEE ANTES DE GENERAR
══════════════════════════════════════════════════════
1. EXACTAMENTE 10 objetos en el array. Ni 9, ni 11. DIEZ.
2. Cada objeto tiene SOLO estos 4 campos raíz:
   { "exercise_type", "lesson_id", "position", "data" }
3. lesson_id = ${currentLessonId} en LOS 10 EJERCICIOS.
4. El ORDEN de exercise_type por posición es FIJO e INMUTABLE:
   pos 1  → "conversation_spacy_prompt"
   pos 2  → "grammar_notes"
   pos 3  → "quiz"
   pos 4  → "fill_in_the_blank"
   pos 5  → "matching"
   pos 6  → "listening"
   pos 7  → "pronunciation"
   pos 8  → "error_detection"
   pos 9  → "reading"
   pos 10 → "writing"
5. Nivel CEFR del contenido: ${activeLevel}
6. Tema de la lección: "${lessonTopic || "[ESCRIBE EL TEMA AQUÍ]"}"
7. JSON válido: sin comentarios //, sin trailing commas, sin texto fuera del array.

══════════════════════════════════════════════════════
🎓 ESTÁNDAR CAMBRIDGE PARA grammar_notes (pos 2)
   ESTA ES LA PIEZA MÁS IMPORTANTE. AQUÍ NO SE IMPROVISA.
══════════════════════════════════════════════════════

Antes de generar los grammar_notes, identifica el PUNTO GRAMATICAL CENTRAL del tema.
No describas "vocabulario" ni "frases útiles". Enseña GRAMÁTICA REAL con la precisión
de un libro Cambridge (English Grammar in Use, English File, Cutting Edge).

ELIGE UNO de estos enfoques según el tema y nivel ${activeLevel}:

  A1-A2 → Estructura básica: afirmativa / negativa / interrogativa + ejemplos cotidianos
  B1-B2 → Contraste entre formas (e.g. Present Perfect vs Simple Past, Gerund vs Infinitive)
  C1-C2 → Matices avanzados: inversión, subjuntivo, condicionales mixtos, énfasis, elipsis

ESTRUCTURA OBLIGATORIA DE LOS 4 POINTS (mínimo 4, máximo 6):

  point 1 · "🧠 La Regla de Oro" 
    → Formula la regla gramatical con precisión lingüística.
    → Incluye la ESTRUCTURA con corchetes: [Sujeto] + [auxiliar] + [verbo en forma X] + [complemento]
    → Da 2 ejemplos: uno afirmativo y uno negativo.
    → Contrasta con el español si hay falsa equivalencia.

  point 2 · "⚡ Cuándo Usarlo (Función Comunicativa)"
    → Explica el SIGNIFICADO COMUNICATIVO, no solo la forma.
    → Usa contextos reales del tema "${lessonTopic || "la lección"}".
    → Incluye al menos 3 mini-ejemplos en contexto con traducción entre paréntesis.
    → Si hay dos o más funciones distintas, sepáralas con ▸

  point 3 · "🔬 El Detalle que Cambia Todo (Regla Avanzada)"
    → Excepción fonológica, ortográfica o semántica relevante para ${activeLevel}.
    → Palabras irregulares, verbos de estado que no admiten progresivo, 
      preposiciones fijas, colocaciones obligatorias, etc.
    → Tabla comparativa si aplica: | Forma ✅ | Forma ❌ | Por qué |

  point 4 · "🚫 Errores de Hablantes de Español (Interferencia L1)"
    → Mínimo 3 errores reales que cometen hispanohablantes con esta estructura.
    → Formato obligatorio:
      ❌ "[Oración con el error típico]"  
      ✅ "[Corrección]"  
      💡 "[Explicación breve de por qué ocurre el error]"

  point 5 (opcional, recomendado B1+) · "🚀 Nivel Pro: Matices de Registro"
    → Diferencia entre uso formal/informal/escrito/hablado.
    → Ejemplos de cómo el mismo significado cambia de registro.
    → Tip de pronunciación o entonación si es relevante.

PROHIBIDO en grammar_notes:
  ✗ Listas de vocabulario sin gramática
  ✗ "Useful phrases" sin explicar su estructura
  ✗ Points vagos como "Practica mucho" o "Es importante"
  ✗ Repetir el mismo ejemplo con palabras distintas
  ✗ Mezclar dos puntos gramaticales distintos sin conexión

REQUERIDO en grammar_notes:
  ✓ Mínimo un diagrama de estructura con corchetes []
  ✓ Contraste ✅/❌ en al menos 2 points
  ✓ Ejemplos específicos del tema "${lessonTopic || "la lección"}"
  ✓ Lenguaje accesible pero sin sacrificar precisión lingüística
  ✓ Que el estudiante termine con UNA regla clara que pueda aplicar inmediatamente

══════════════════════════════════════════════════════
📐 ESQUEMAS EXACTOS POR TIPO — Copia las KEYS, cambia los VALUES
══════════════════════════════════════════════════════

┌─ pos 1 · conversation_spacy_prompt ───────────────
"data": {
  "lesson_introduction": "Hello, Student! I'm Spacy. Today we'll master [punto gramatical central] through [tema]. Let's go!",
  "learning_objectives": [
    "Bienvenida y diagnóstico previo",
    "Estructura gramatical: [punto central]",
    "Función comunicativa en contexto",
    "Práctica controlada (forma correcta)",
    "Práctica libre (significado real)",
    "Juego de roles: [situación del tema]",
    "Autoevaluación y corrección de errores",
    "Lección Completada 🎓"
  ],
  "visual_gallery_cards": [
    { "trigger_topic": "estructura_gramatical", "card_id": "V1", "content": "Diagrama: [Sujeto] + [auxiliar] + [verbo] → ejemplo completo" },
    { "trigger_topic": "errores_comunes", "card_id": "V2", "content": "❌ Error típico → ✅ Forma correcta + regla" }
  ]
}

┌─ pos 2 · grammar_notes ────────────────────────────
"data": {
  "title": "[Emoji] Masterclass: [Punto Gramatical Central en Contexto de ${lessonTopic || "el tema"}]",
  "points": [
    {
      "header": "🧠 La Regla de Oro: [Nombre de la Estructura]",
      "content": "Estructura: [Sujeto] + [verbo] + [complemento]\\n\\n✅ She **has been working** here for five years.\\n❌ She **is working** here since five years.\\n\\n⚠️ En español decimos 'desde hace', pero en inglés usamos **for** (duración) o **since** (punto de inicio)."
    },
    {
      "header": "⚡ Cuándo Usarlo: Función Comunicativa",
      "content": "Usamos esta estructura para:\\n▸ **Acción iniciada en el pasado que continúa ahora**\\n   → 'I **have lived** here since 2010.' (y sigo viviendo aquí)\\n▸ **Resultado presente de acción pasada**\\n   → 'She **has cleaned** the house.' (está limpia ahora)\\n▸ **Experiencia de vida sin tiempo definido**\\n   → 'He **has visited** Paris three times.'"
    },
    {
      "header": "🔬 El Detalle que Cambia Todo",
      "content": "| Tiempo | Marcador | Ejemplo |\\n|--------|----------|---------|\\n| For | Duración | for **two hours** / for **years** |\\n| Since | Punto inicio | since **Monday** / since **2020** |\\n| Just | Muy reciente | I've **just** arrived ✅ |\\n\\n🚨 **Verbos de estado** (know, love, believe, want) NO usan la forma progresiva:\\n❌ I have been knowing her for years.\\n✅ I have known her for years."
    },
    {
      "header": "🚫 Errores de Hispanohablantes",
      "content": "❌ 'I am living here since five years.'\\n✅ 'I have lived here for five years.'\\n💡 El español usa presente simple ('vivo aquí'), el inglés exige Present Perfect.\\n\\n❌ 'Did you ever visit London?'\\n✅ 'Have you ever visited London?'\\n💡 'Ever' con experiencias de vida → siempre Present Perfect, no Past Simple.\\n\\n❌ 'I have seen him yesterday.'\\n✅ 'I saw him yesterday.'\\n💡 'Yesterday' es tiempo definido → Past Simple obligatorio."
    }
  ]
}

┌─ pos 3 · quiz ─────────────────────────────────────
"data": {
  "title": "Quiz: [tema]",
  "instruction": "Selecciona la opción gramaticalmente correcta.",
  "xp": 20,
  "questions": [
    { "id": "q1", "question": "¿Pregunta?", "options": ["Opción A", "Opción B", "Opción C"], "answer": "Opción A" }
  ]
}
🚨 "answer" = TEXTO EXACTO de "options". NUNCA índice numérico.
🚨 Mínimo 5 questions. Las opciones incorrectas deben ser errores reales, no absurdos.

┌─ pos 4 · fill_in_the_blank ────────────────────────
"data": {
  "title": "Completa las Oraciones",
  "instruction": "Escribe la forma correcta para completar cada oración.",
  "xp": 15,
  "blanks": [
    { "id": "b1", "before_blank": "She", "after_blank": "here since 2018. (live)", "answer": "has lived", "alternate_answer": "has lived", "hint": "Present Perfect: have/has + participio pasado", "emoji": "💡" }
  ]
}
🚨 Mínimo 5 blanks. Cada blank debe tener hint explicando la regla.

┌─ pos 5 · matching ─────────────────────────────────
"data": {
  "title": "Empareja: Forma y Función",
  "instruction": "Une cada oración con su función gramatical.",
  "xp": 50,
  "pairs":   [ {"id":"pair1","content":"Término 1"}, {"id":"pair2","content":"Término 2"}, {"id":"pair3","content":"Término 3"}, {"id":"pair4","content":"Término 4"}, {"id":"pair5","content":"Término 5"} ],
  "matches": [ {"id":"match1","content":"Definición A"}, {"id":"match2","content":"Definición B"}, {"id":"match3","content":"Definición C"}, {"id":"match4","content":"Definición D"}, {"id":"match5","content":"Definición E"} ],
  "solution": { "pair1":"match3", "pair2":"match5", "pair3":"match1", "pair4":"match2", "pair5":"match4" }
}
🚨 IDs en solution DEBEN existir en pairs y matches. Mezcla el solution (NO 1→1, 2→2).

┌─ pos 6 · listening ────────────────────────────────
"data": {
  "title": "Listening: [tema en contexto real]",
  "instruction": "Escucha el audio y responde. Presta atención a las estructuras gramaticales.",
  "xp": 50,
  "audio_url": "https://res.cloudinary.com/PLACEHOLDER/audio_${(lessonTopic || "lesson").replace(/\s+/g, "_").toLowerCase()}.mp3",
  "timeLimit": 180,
  "transcript": "A: [diálogo natural con 3-4 turnos que use la gramática de la lección]\\nB: [respuesta con vocabulario del tema]\\nA: [continuación]\\nB: [cierre]",
  "questions": [
    { "id": "q1", "question": "¿Pregunta de comprensión?", "options": ["Opción A", "Opción B", "Opción C", "Opción D"], "answer": "Opción A" }
  ]
}
🚨 "answer" texto exacto de "options". Mínimo 5 questions. Transcript = diálogo real, no artificial.

┌─ pos 7 · pronunciation ────────────────────────────
"data": {
  "title": "Pronunciación: Sonidos Clave",
  "instruction": "Escucha, repite y grábate. ¡Compara tu acento con el nativo!",
  "xp": 40,
  "words_or_sentences": [
    { "text": "I have worked here for years", "phonetic": "/aɪ həv wɜːrkt hɪər fər jɪərz/" }
  ]
}
🚨 Mínimo 8 items. IPA correcto con barras //. Incluye oraciones completas, no solo palabras sueltas.

┌─ pos 8 · error_detection ──────────────────────────
"data": {
  "title": "Detective Gramatical: ¿Correcto o Incorrecto?",
  "instruction": "Detecta el error gramatical. Recuerda las reglas de la lección.",
  "xp": 20,
  "sentences": [
    { "id": "e1", "sentence": "Oración con o sin error.", "options": ["Correct", "Incorrect"], "answer": "Incorrect", "explanation": "Error: [qué está mal]. Correcto: '[versión correcta]'. Regla: [por qué]." }
  ]
}
🚨 "answer" SOLO "Correct" o "Incorrect" (mayúsculas exactas). Mínimo 5. Mezcla correctas e incorrectas.

┌─ pos 9 · reading ──────────────────────────────────
"data": {
  "title": "Lectura: [título evocador sobre ${lessonTopic || "el tema"}]",
  "instruction": "Lee el texto. Pon atención a las estructuras gramaticales en **negrita**.",
  "xp": 25,
  "text": "[Texto narrativo/descriptivo de mínimo 150 palabras. Usa **negritas** para ejemplos de la gramática estudiada. Incluye detalles específicos que serán preguntados en el quiz.]",
  "comprehension_quiz": {
    "questions": [
      { "id": "rq1", "question": "¿Pregunta?", "options": ["A", "B", "C"], "answer": "A" }
    ]
  }
}
🚨 Mínimo 5 questions. "answer" debe existir en "options". Texto mínimo 150 palabras.

┌─ pos 10 · writing ─────────────────────────────────
"data": {
  "title": "¡Tu Turno de Brillar!",
  "instruction": "Escribe un párrafo de 4-6 oraciones usando [estructura gramatical] sobre [contexto del tema]. Incluye: [elemento 1], [elemento 2] y [elemento 3].",
  "xp": 50,
  "theme": "[Situación real y motivadora relacionada con ${lessonTopic || "el tema"} que invite a usar la gramática aprendida]."
}

══════════════════════════════════════════════════════
✅ CHECKLIST FINAL — VERIFICA ANTES DE ENTREGAR
══════════════════════════════════════════════════════
□ ¿El array tiene EXACTAMENTE 10 elementos?
□ ¿Todos tienen lesson_id = ${currentLessonId}?
□ ¿El orden de exercise_type sigue la secuencia canónica?
□ ¿grammar_notes tiene mínimo 4 points con gramática real Cambridge (no solo vocabulario)?
□ ¿Todos los "answer" de quiz/listening/reading son texto exacto de sus "options"?
□ ¿error_detection usa "Correct"/"Incorrect" con C e I mayúsculas?
□ ¿matching.solution usa IDs que existen en pairs y matches y no es 1→1?
□ ¿El JSON es válido? (sin //, sin trailing commas, sin texto fuera del array)

GENERA LOS 10 EJERCICIOS AHORA. DEVUELVE SOLO EL ARRAY JSON.`;

  /* ════ Validators & Handlers ════ */

 

  const runValidation=(text)=>{
    const raw=text||jsonText;if(!raw.trim())return;
    setMode("validating");setValStep(-1);setValidation(null);
    const result=validateJSON(raw);
    const checks=buildChecks(result);
    let step=0;
    const tick=()=>{setValStep(step);step++;
      if(step<checks.length)timerRef.current=setTimeout(tick,240);
      else{setValidation({...result,checks});setMode("validated");}};
    timerRef.current=setTimeout(tick,160);
  };

  // ── Ponlo ANTES de runSeed ──────────────────────────
const SEED_STEPS = [
  "Conectando a Neon.tech...",
  "Preparando transacción SQL (Bulk Insert)...",
  "Insertando 10 ejercicios en tabla exercises...",
  "🤖 Espejando a spacy_lessons (tutor AI)...",
  "Indexando posiciones y tipos...",
  "Verificando integridad referencial...",
  "✅ Lección sembrada correctamente!"
];

 const runSeed = async () => {
  if (!validation || !validation.ok) return;
  setMode("seeding"); setSeedStep(0);
  try {
    const token = localStorage.getItem('token');

    // ── Un solo fetch: el backend hace todo (exercises + mirror) ──
    const res = await fetch('/api/admin/exercises/bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(validation.parsed)
    });

    const result = await res.json();
    if (result.spacy_mirror?.created) {
      nslToast.info(`🤖 Espejo Spacy creado (ID ${result.spacy_mirror.lesson_id})`);
    }

  } catch(e) { console.error(e); }

  let i = 0;
  const tick = () => {
    i++; setSeedStep(i);
    if (i < SEED_STEPS.length - 1) timerRef.current = setTimeout(tick, 420);
    else setTimeout(() => { setMode("seeded"); fetchTree(); }, 400);
  };
  timerRef.current = setTimeout(tick, 400);
};
  

  const handleDrop=useCallback((e)=>{
    e.preventDefault();setDragOver(false);
    const file=e.dataTransfer.files[0];if(!file)return;
    if(!file.name.endsWith(".json")){nslToast.error("Solo se aceptan archivos .json");return;}
    setDroppedFile(file.name);
    const reader=new FileReader();
    reader.onload=(ev)=>{const text=ev.target.result;setJsonText(text);setInputMode("code");runValidation(text);};
    reader.readAsText(file);
  },[jsonText]);

  const handleFileInput=(e)=>{
    const file=e.target.files[0];if(!file)return;setDroppedFile(file.name);
    const reader=new FileReader();
    reader.onload=(ev)=>{const text=ev.target.result;setJsonText(text);setInputMode("code");runValidation(text);};
    reader.readAsText(file);
  };

  const resetInjector=()=>{clearTimeout(timerRef.current);setMode("inject");setJsonText("");
    setDroppedFile(null);setValidation(null);setValStep(-1);setSeedStep(-1);};

  const startNewLesson=()=>{
    setSelLessonId(null);setMode("create_shell");setJsonText("");
    setDroppedFile(null);setValidation(null);setValStep(-1);setSeedStep(-1);
    setInputMode("drop");setNewLessonTitle("");setLessonTopic("");
  };

  const handleCreateLessonShell = async () => {
    if (!newLessonTitle.trim()) return;
    setIsCreatingLesson(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/admin/exercises/lessons', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ title: newLessonTitle, description: "Lección en construcción", level_code: activeLevel })
      });
      if (res.ok) {
        const data = await res.json();
        setSelLessonId(data.lesson_id);
        setLessonTopic(data.title);
        setMode("preview");
        fetchTree();
        nslToast.success(`Lección "${newLessonTitle}" creada con ID ${data.lesson_id} ✓`);
      } else {
        nslToast.error("Error al crear la lección en la base de datos");
      }
    } catch(e) { console.error(e); nslToast.error("Error de red al crear la lección"); }
    finally { setIsCreatingLesson(false); }
  };

  const handleDeleteLesson = () => {
    nslConfirm(
      "🗑️ Eliminar Lección",
      `Estás a punto de eliminar permanentemente la lección ID ${currentLessonId} y todos sus ejercicios. Esta acción es irreversible y afecta a la base de datos en producción.`,
      async () => {
        setConfirmCfg(null);
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`/api/admin/exercises/lessons/${currentLessonId}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if(res.ok) {
            nslToast.success(`Lección ID ${currentLessonId} eliminada correctamente.`);
            fetchTree();
            setMode("idle");
            setSelLessonId(null);
          } else {
            nslToast.error("Error al eliminar la lección en producción.");
          }
        } catch(e) { console.error(e); nslToast.error("Error de red al eliminar."); }
      },
      { confirmLabel:"Eliminar definitivamente", dangerLevel:"high" }
    );
  };

  const handleLessonDrop = useCallback((targetId) => {
    if (!dragLessonId || dragLessonId === targetId) return;
    const currentList = [...(lessons[activeLevel] || [])];
    const fromIdx = currentList.findIndex(l => l.id === dragLessonId);
    const toIdx   = currentList.findIndex(l => l.id === targetId);
    if (fromIdx === -1 || toIdx === -1) return;
 
    const reordered = [...currentList];
    const [moved]   = reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, moved);
    const withNewPos = reordered.map((l, i) => ({ ...l, pos: i + 1 }));
 
    setLessons(prev => ({ ...prev, [activeLevel]: withNewPos }));
    setDragOverLessonId(null);
    setDragLessonId(null);
 
    const token = localStorage.getItem('token');
    fetch('/api/admin/exercises/lessons/reorder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ lesson_ids: withNewPos.map(l => l.id) })
    })
    .then(r => r.json())
    .then(result => {
      if (result.ok) nslToast.success('Orden guardado en Neon.tech ✓');
      else { nslToast.error('Error al guardar el orden'); fetchTree(); }
    })
    .catch(() => { nslToast.error('Error de red al reordenar'); fetchTree(); });
  }, [dragLessonId, lessons, activeLevel]);
 
  const handleLessonDragEnd = useCallback(() => {
    setDragLessonId(null);
    setDragOverLessonId(null);
  }, []);

  useEffect(()=>()=>clearTimeout(timerRef.current),[]);

  /* ════════════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════════════ */
  return (
    <>
      {/* ── Toast Container ── */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} dark={dark} t={t}/>

      {/* ── Confirm Dialog ── */}
      {confirmCfg && <ConfirmDialog config={confirmCfg} t={t} dark={dark}/>}

      <div style={{display:"flex",flexDirection:"column",gap:14}}>

        {/* LEVEL PILLS */}
        <div style={{background:t.bg,borderRadius:20,padding:"13px 16px",boxShadow:t.nmd,
                     display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginRight:4,flexShrink:0}}>
            <div style={{width:22,height:22,borderRadius:6,display:"flex",alignItems:"center",
                         justifyContent:"center",background:(levelColor+"1a"),color:levelColor}}>
              <BookOpen size={11}/>
            </div>
            <span style={{fontSize:10,fontWeight:700,letterSpacing:".12em",
                          textTransform:"uppercase",color:t.tx2}}>CEFR</span>
          </div>
          {LEVELS.map(lvl=>{
            const a=activeLevel===lvl.id;
            return (
              <button key={lvl.id} onClick={()=>{setActiveLevel(lvl.id);setMode("idle");setSelLessonId(null);}}
                style={{display:"flex",alignItems:"center",gap:4,padding:"5px 13px",
                        borderRadius:50,border:"none",cursor:"pointer",
                        fontFamily:"Inter,sans-serif",transition:"all .2s",
                        background:a?(lvl.color+"18"):t.bg,
                        boxShadow:a?(t.ism+",0 0 0 1.5px "+lvl.color+"88"):t.ixs,
                        color:a?lvl.color:t.tx2}}>
                <span style={{fontSize:12,fontWeight:900,fontFamily:"JetBrains Mono,monospace"}}>{lvl.label}</span>
                {a&&<span style={{width:5,height:5,borderRadius:"50%",background:lvl.color,
                                  display:"inline-block",flexShrink:0,
                                  boxShadow:dark?("0 0 6px "+lvl.color):"",
                                  animation:"cmsPls 2s ease-in-out infinite"}}/>}
              </button>
            );
          })}
          <div style={{marginLeft:"auto",padding:"3px 9px",borderRadius:50,background:t.bg,
                       boxShadow:t.ixs,flexShrink:0}}>
            <span style={{fontSize:10,fontWeight:700,color:levelColor,
                          fontFamily:"JetBrains Mono,monospace"}}>
              {(lessons[activeLevel]||[]).length} lecciones
            </span>
          </div>
        </div>

        {/* BENTO GRID */}
        <div style={{display:"grid",gap:14,gridTemplateColumns:isMobile?"1fr":"260px 1fr"}}>

          {/* LEFT: LESSON TREE */}
          <div style={{background:t.bg,borderRadius:20,padding:14,boxShadow:t.nmd,
                       display:"flex",flexDirection:"column",gap:8}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:t.tx}}>Árbol de Lecciones</div>
              <div style={{fontSize:9,color:t.tx2,marginTop:2}}>
                Nivel <span style={{color:levelColor,fontWeight:700}}>{activeLevel}</span>
              </div>
            </div>
            <button onClick={startNewLesson}
              style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",borderRadius:11,
                      border:("1px solid "+t.er+"44"),cursor:"pointer",
                      background:dark?(t.er+"0e"):(t.er+"08"),
                      boxShadow:(t.nxs+",0 0 0 1px "+t.er+"33"),color:t.er,
                      fontSize:11,fontWeight:700,fontFamily:"Inter,sans-serif",transition:"all .2s",
                      textShadow:dark?("0 0 8px "+t.er+"55"):""}}>
              <Plus size={11}/> Nueva Lección
            </button>
            <div style={{display:"flex",flexDirection:"column",gap:4,maxHeight:500,overflowY:"auto"}}
               onDragOver={e=>e.preventDefault()}>
            {(lessons[activeLevel]||[]).map(lesson=>{
              const isSel=selLessonId===lesson.id;
              const isHov=hovLessonId===lesson.id;
              const isDragging=dragLessonId===lesson.id;
              const isDragOver=dragOverLessonId===lesson.id;
              const sc=STATUS_CFG[lesson.status]||STATUS_CFG.empty;
              return (
                <div key={lesson.id}
                  draggable
                  onDragStart={()=>setDragLessonId(lesson.id)}
                  onDragEnd={handleLessonDragEnd}
                  onDragEnter={()=>setDragOverLessonId(lesson.id)}
                  onDragLeave={()=>setDragOverLessonId(null)}
                  onDrop={()=>handleLessonDrop(lesson.id)}
                  onMouseEnter={()=>setHovLessonId(lesson.id)}
                  onMouseLeave={()=>setHovLessonId(null)}
                  onClick={()=>{setSelLessonId(lesson.id);setMode("preview");}}
                  style={{background:t.bg,borderRadius:11,padding:"8px 10px",cursor:"pointer",
                          transition:"all .18s",opacity:isDragging?.4:1,
                          transform:isDragOver?"translateY(-2px)":"translateY(0)",
                          boxShadow:isDragOver
                            ?(t.ism+",0 0 0 2px "+levelColor+"99")
                            :isSel?(t.ism+",0 0 0 1.5px "+levelColor+"66")
                            :isHov?t.nxs:t.ixs,
                          borderLeft:("2.5px solid "+(isSel?levelColor:(isDragOver?levelColor:(levelColor+"22"))))}}>
                  <div style={{display:"flex",alignItems:"center",gap:7}}>
                    <div style={{color:t.tx2,flexShrink:0,cursor:"grab",opacity:.4,
                                 display:"flex",alignItems:"center"}}>
                      <GripVertical size={12}/>
                    </div>
                    <div style={{width:18,height:18,borderRadius:5,flexShrink:0,display:"flex",
                                 alignItems:"center",justifyContent:"center",
                                 background:isSel?(levelColor+"22"):t.bg,
                                 boxShadow:t.ixs,fontSize:9,fontWeight:800,
                                 color:isSel?levelColor:t.tx2,
                                 fontFamily:"JetBrains Mono,monospace"}}>{lesson.pos}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:isSel?700:500,color:isSel?levelColor:t.tx,
                                   whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
                                   lineHeight:1.3,marginBottom:2}}>{lesson.title}</div>
                      <div style={{display:"flex",gap:4,alignItems:"center"}}>
                        {sc.dot&&<span style={{width:5,height:5,borderRadius:"50%",
                                               background:sc.color,flexShrink:0,
                                               boxShadow:dark?("0 0 4px "+sc.color):""}}/>}
                        <span style={{fontSize:8,color:sc.color,fontWeight:600}}>{sc.label}</span>
                        {lesson.exercises>0&&(
                          <span style={{fontSize:8,color:t.tx2}}>· {lesson.exercises} ej</span>)}
                      </div>
                    </div>
                    {isHov&&!isDragging&&<ChevronRight size={11} style={{color:levelColor,flexShrink:0}}/>}
                  </div>
                </div>
              );
            })}
          </div>
            <div style={{padding:"8px 10px",borderRadius:10,background:t.bg,
                         boxShadow:t.ixs,marginTop:"auto"}}>
              <div style={{display:"flex",alignItems:"center",gap:5,marginBottom:3}}>
                <Database size={10} style={{color:dark?"#a855f7":"#7c3aed"}}/>
                <span style={{fontSize:9,fontWeight:700,color:dark?"#a855f7":"#7c3aed",
                              textTransform:"uppercase",letterSpacing:".1em"}}>Neon.tech</span>
                <div style={{marginLeft:"auto",width:6,height:6,borderRadius:"50%",
                             background:t.ok,boxShadow:dark?("0 0 5px "+t.ok):"",
                             animation:"cmsPls 2s ease-in-out infinite"}}/>
              </div>
              <div style={{fontSize:9,color:t.tx2}}>
                tabla: <span style={{fontFamily:"JetBrains Mono,monospace",color:HICOLOR}}>exercises</span>
              </div>
            </div>
          </div>

          {/* RIGHT COL */}
          <div style={{display:"flex",flexDirection:"column",gap:14}}>

            {/* MAIN PANEL */}
            <div style={{background:t.bg,borderRadius:20,padding:18,boxShadow:t.nmd,minHeight:480}}>

              {mode==="idle"&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                             justifyContent:"center",flex:1,gap:14,padding:"32px 0"}}>
                  <div style={{width:56,height:56,borderRadius:16,display:"flex",alignItems:"center",
                               justifyContent:"center",background:(levelColor+"14"),color:levelColor,
                               boxShadow:dark?("0 0 24px "+levelColor+"22"):t.nmd}}>
                    <FileJson size={24}/>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:15,fontWeight:700,color:t.tx,marginBottom:6}}>
                      Quirófano de Inyección JSON
                    </div>
                    <div style={{fontSize:11,color:t.tx2,lineHeight:1.6,maxWidth:340}}>
                      Selecciona una lección para ver y editar sus ejercicios,
                      o crea una nueva inyectando su JSON.
                    </div>
                  </div>
                </div>
              )}

              {mode==="create_shell"&&(
                <div style={{display:"flex",flexDirection:"column",alignItems:"center",
                             justifyContent:"center",flex:1,gap:16,padding:"32px 0",
                             animation:"cmsFadeUp .25s ease"}}>
                  <div style={{width:48,height:48,borderRadius:14,display:"flex",alignItems:"center",
                               justifyContent:"center",background:(t.er+"14"),color:t.er,boxShadow:t.nxs}}>
                    <Plus size={20}/>
                  </div>
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:16,fontWeight:800,color:t.tx,marginBottom:4}}>Crear Nueva Lección</div>
                    <div style={{fontSize:11,color:t.tx2}}>
                      Primero creamos la "habitación" vacía en Neon.tech para obtener un ID seguro.
                    </div>
                  </div>
                  <div style={{width:"100%",maxWidth:360,marginTop:8}}>
                    <label style={{fontSize:10,fontWeight:700,color:t.tx2,marginBottom:6,display:"block"}}>
                      Título de la Lección
                    </label>
                    <div style={{background:t.bg,borderRadius:12,padding:"2px 6px",
                                 boxShadow:("inset -3px 3px 8px "+t.sd+",inset 3px -3px 8px "+t.sl)}}>
                      <input type="text" value={newLessonTitle} onChange={(e)=>setNewLessonTitle(e.target.value)}
                        placeholder="Ej: Parts of the House"
                        style={{width:"100%",background:"transparent",border:"none",outline:"none",
                                padding:"10px 8px",color:t.tx,fontSize:13,fontWeight:600,
                                fontFamily:"Inter,sans-serif"}}/>
                    </div>
                  </div>
                  <button disabled={!newLessonTitle.trim()||isCreatingLesson} onClick={handleCreateLessonShell}
                    style={{marginTop:10,padding:"12px 24px",borderRadius:12,
                            border:("1px solid "+HICOLOR+"44"),
                            background:!newLessonTitle.trim()?"transparent":(HICOLOR+"14"),
                            color:!newLessonTitle.trim()?t.tx2:HICOLOR,
                            fontSize:13,fontWeight:800,fontFamily:"Inter,sans-serif",
                            cursor:!newLessonTitle.trim()?"not-allowed":"pointer",
                            display:"flex",alignItems:"center",gap:8,transition:"all .2s",
                            boxShadow:newLessonTitle.trim()?(t.nxs+",0 0 0 1px "+HICOLOR+"33"):"none"}}>
                    {isCreatingLesson?<Loader2 size={16} className="lucide-spin"/>:<Database size={14}/>}
                    {isCreatingLesson?"Construyendo...":"Crear Base en Neon.tech"}
                  </button>
                </div>
              )}

              {mode==="preview"&&selLessonId&&(
                
                <LessonPreview
                  lesson={(lessons[activeLevel]||[]).find(l=>l.id===selLessonId)}
                  t={t} dark={dark} HICOLOR={HICOLOR} levelColor={levelColor}
                  onInject={()=>setMode("inject")}
                  onDelete={handleDeleteLesson}/>
              )}

              {["inject","validating","validated","seeding","seeded"].includes(mode)&&(
                <div style={{display:"flex",flexDirection:"column",gap:14}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:32,height:32,borderRadius:10,display:"flex",
                                 alignItems:"center",justifyContent:"center",
                                 background:(t.er+"14"),color:t.er,flexShrink:0}}>
                      <FileJson size={14}/>
                    </div>
                    <div>
                      <div style={{fontSize:14,fontWeight:700,color:t.tx}}>Inyector de Lección JSON</div>
                      <div style={{fontSize:10,color:t.tx2}}>
                        Destino: <span style={{color:levelColor,fontWeight:700}}>ID {currentLessonId}</span> · Safe Seeding
                      </div>
                    </div>
                    {mode!=="seeding"&&mode!=="seeded"&&(
                      <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
                        {selLessonId&&(
                          <button onClick={handleDeleteLesson}
                            style={{display:"flex",alignItems:"center",gap:4,
                                    padding:"4px 10px",borderRadius:8,border:(`1px solid ${t.er}44`),
                                    background:dark?(`${t.er}1a`):(`${t.er}14`),color:t.er,fontSize:10,
                                    fontWeight:700,cursor:"pointer",fontFamily:"Inter,sans-serif"}}>
                            <Trash2 size={10}/> Borrar
                          </button>
                        )}
                        <button onClick={resetInjector}
                          style={{display:"flex",alignItems:"center",gap:4,
                                  padding:"4px 10px",borderRadius:8,border:("1px solid "+t.tx2+"33"),
                                  background:"transparent",color:t.tx2,fontSize:10,
                                  fontWeight:600,cursor:"pointer",fontFamily:"Inter,sans-serif"}}>
                          <RefreshCw size={9}/> Limpiar
                        </button>
                      </div>
                    )}
                  </div>

                  {mode==="inject"&&(
                    <div style={{background:t.bg,borderRadius:12,padding:3,boxShadow:t.ixs,display:"flex",gap:2}}>
                      {[{id:"drop",label:"Drag & Drop"},{id:"code",label:"Editor de Código"}].map(tb=>{
                        const a=inputMode===tb.id;
                        return (
                          <button key={tb.id} onClick={()=>setInputMode(tb.id)}
                            style={{flex:1,padding:"7px 6px",borderRadius:9,border:"none",cursor:"pointer",
                                    fontSize:11,fontWeight:a?700:500,fontFamily:"Inter,sans-serif",
                                    background:a?t.bg:"transparent",
                                    boxShadow:a?(t.nxs+",0 0 0 1.5px "+t.er+"55"):"none",
                                    color:a?t.er:t.tx2,transition:"all .18s"}}>
                            {tb.id==="drop"?"📁 "+tb.label:"💻 "+tb.label}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {mode==="inject"&&inputMode==="drop"&&(
                    <div onDragOver={e=>{e.preventDefault();setDragOver(true);}}
                         onDragLeave={()=>setDragOver(false)} onDrop={handleDrop}
                         onClick={()=>fileRef.current&&fileRef.current.click()}
                         style={{position:"relative",borderRadius:16,padding:"40px 24px",
                                 cursor:"pointer",transition:"all .25s",display:"flex",
                                 flexDirection:"column",alignItems:"center",justifyContent:"center",gap:14,
                                 background:dragOver?(dark?(t.er+"0e"):(t.er+"08")):(dark?"rgba(255,255,255,.015)":"rgba(0,0,0,.02)"),
                                 border:("2px dashed "+(dragOver?t.er:(dark?"rgba(255,255,255,.12)":"rgba(0,0,0,.14)")))}}>
                      <div style={{width:52,height:52,borderRadius:14,display:"flex",
                                   alignItems:"center",justifyContent:"center",
                                   background:dragOver?(t.er+"18"):t.bg,boxShadow:t.nmd,color:t.er}}>
                        <Upload size={22} style={{animation:dragOver?"cmsBounce .6s ease-in-out infinite":"none"}}/>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <div style={{fontSize:14,fontWeight:700,color:dragOver?t.er:t.tx,marginBottom:5}}>
                          {dragOver?"Suelta el archivo aquí":"Arrastra tu archivo .json aquí"}
                        </div>
                        <div style={{fontSize:11,color:t.tx2}}>o haz click para seleccionar</div>
                      </div>
                      <input ref={fileRef} type="file" accept=".json"
                        onChange={handleFileInput} style={{display:"none"}}/>
                    </div>
                  )}

                  {mode==="inject"&&inputMode==="code"&&(
                    <div>
                      {droppedFile&&(
                        <div style={{display:"flex",alignItems:"center",gap:6,padding:"5px 10px",
                                     borderRadius:8,background:dark?(t.ok+"0e"):(t.ok+"08"),
                                     border:("1px solid "+t.ok+"33"),marginBottom:8}}>
                          <FileJson size={10} style={{color:t.ok}}/>
                          <span style={{fontSize:10,fontWeight:600,color:t.ok}}>{droppedFile}</span>
                          <span style={{fontSize:9,color:t.tx2,marginLeft:"auto"}}>
                            {jsonText.length.toLocaleString()} chars
                          </span>
                        </div>
                      )}
                      <div className="cms-code-editor" style={{borderRadius:10,overflow:"hidden"}}>
                        <div style={{display:"flex",alignItems:"center",gap:5,padding:"5px 10px",
                                     background:dark?"#0d0d1a":"#1e1e2e",
                                     borderBottom:"1px solid rgba(255,255,255,.05)"}}>
                          {["#ff5f57","#febc2e","#28c840"].map(c=>(
                            <div key={c} style={{width:8,height:8,borderRadius:"50%",background:c}}/>))}
                          <span style={{fontSize:8,fontFamily:"JetBrains Mono,monospace",
                                        color:"rgba(255,255,255,.25)",marginLeft:6}}>
                            exercises.json · Safe Seeding
                          </span>
                        </div>
                        <textarea value={jsonText} onChange={e=>setJsonText(e.target.value)}
                          onFocus={()=>setCodeFocus(true)} onBlur={()=>setCodeFocus(false)}
                          spellCheck={false} rows={16}
                          placeholder={'[\n  {\n    "exercise_type": "conversation_spacy_prompt",\n    "lesson_id": 125,\n    "position": 1,\n    "data": { ... }\n  },\n  ...\n]'}
                          style={{width:"100%",background:dark?"#0d1117":"#1e1e2e",
                                  color:dark?"#00ff88":"#4ade80",border:"none",outline:"none",
                                  boxShadow:codeFocus?("0 0 0 2px "+HICOLOR+"55"):"none",
                                  padding:"12px 14px",fontSize:11,
                                  fontFamily:"JetBrains Mono,monospace",resize:"vertical",
                                  transition:"box-shadow .2s"}}/>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",marginTop:4}}>
                        <span style={{fontSize:9,color:t.tx2}}>{jsonText.length.toLocaleString()} chars</span>
                        <span style={{fontSize:9,color:t.tx2}}>{jsonText.split("\n").length} líneas</span>
                      </div>
                    </div>
                  )}

                  {mode==="inject"&&(
                    <button disabled={!jsonText.trim()} onClick={()=>runValidation(jsonText)}
                      style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                              padding:"12px",borderRadius:13,border:("1px solid "+HICOLOR+"44"),
                              cursor:!jsonText.trim()?"not-allowed":"pointer",
                              background:!jsonText.trim()?"transparent":(dark?(HICOLOR+"14"):(HICOLOR+"0e")),
                              color:!jsonText.trim()?t.tx2:HICOLOR,
                              fontSize:13,fontWeight:800,fontFamily:"Inter,sans-serif",
                              opacity:!jsonText.trim()?.35:1,transition:"all .2s",
                              textShadow:dark&&jsonText.trim()?("0 0 10px "+HICOLOR+"77"):""}}>
                      <FlaskConical size={14}/> Validar ADN de Lección (10 checks)
                    </button>
                  )}

                  {mode==="validating"&&<ValidatingScreen t={t} HICOLOR={HICOLOR}/>}
                  {mode==="validated"&&validation&&(
                    <ValidationResult t={t} dark={dark} HICOLOR={HICOLOR} levelColor={levelColor}
                      validation={validation} onSeed={runSeed} onReset={resetInjector}/>
                  )}
                  {mode==="seeding"&&(
                    <SeedingScreen t={t} dark={dark} HICOLOR={HICOLOR} step={seedStep} steps={SEED_STEPS}/>
                  )}
                  {mode==="seeded"&&(
                    <SeededScreen t={t} dark={dark} HICOLOR={HICOLOR}
                      validation={validation} onNew={startNewLesson} onBack={()=>setMode("idle")}/>
                  )}
                </div>
              )}
            </div>

            <IndividualPromptGenerator
              t={t}
              dark={dark}
              HICOLOR={HICOLOR}
              lessonId={currentLessonId}
              lessonTopic={lessonTopic}
              setLessonTopic={setLessonTopic}
              activeLevel={activeLevel}
            />
          </div>
        </div>
      </div>
    </>
  );
}